import utilities from './utils';
import LdPatchParser from './ld-patch-parser';
import FormData from 'form-data';
import { LDnoop, LDinsertResource, LDreplaceResource, LDdeleteResource, LDreplace, LDupdate } from './ld-patch-model';
import LdPatchProcessor from './ld-patch-processor';
import { v4 as uuidv4 } from 'uuid';
import io from 'socket.io-client';
import jsonld from 'jsonld';
import TransformProcessor from './transform-processor';
import BridgeTransformProcessor from './bridge-transform-processor';
import ApiManager from './ApiManager';
import FramesManager from './FramesManager';
import cloneDeep from 'lodash/cloneDeep';
import {
	InvalidParameter,
	InvalidCreationException,
	InvalidUpdateOperationException,
	PropertyNotAvailableException,
	NoActiveSubscription,
	AuthException
} from './errors';
import { SNAPSHOT_CONFIGURATION_NAMESPACE, PROJECT_NAMESPACE, USER_NAMESPACE, INVITATION_NAMESPACE, SUBSCRIPTION_NAMESPACE, INTEGRATION_NAMESPACE } from './constants';

const GS = (function () {

	let instance;

	function RDFedit() {

		if (instance) return instance;
		// Create a singleton
		instance = this;

		let GSendpoint;

		const integerXSDtype = "http://www.w3.org/2001/XMLSchema#integer";
		const booleanXSDtype = "http://www.w3.org/2001/XMLSchema#boolean";
		const decimalXSDtype = "http://www.w3.org/2001/XMLSchema#decimal";

		let sessionToken;
		let activeSubscription;
		let activeSubscriptionName = "No subscription selected";
		let activeSubscriptionBucket;
		let activeSubscriptionType;
		let activeSubscriptionMaxImportSize = 1000000;
		let readOnly = false;
		let isInitialised = false;
		let boundObject = {};
		let internalContext = {};
		let isMaterialising = false;
		let unavailableResources = new Set();
		let errorReport = function () { /*  This is intentional */ };
		let importAction;
		let importCompletionCallback = function () { };
		let lockAction;
		let uploadedFilename;
		let socket;
		let isExiting = false;
		let inFlight = new Map();
		let cachedData = new Map();
		const socketEvent = 'gs';
		let socketErrorMessage = "";
		let productInfo;
		let checkingEditsAttempts = 0;
		let loadKey = uuidv4();
		let checkingEdits;
		let checkingIRI;
		let checkingIriAttempts = 0;
		let validation;
		let chainEntailments;
		let tp;
		let btp;
		// We basically give the user session a UUID to identify changes coming
		// from this user in this session
		let sessionUUID;
		// An array of revisions waiting to be sent to the server
		// The actual operations are on the bridge but this serves a useful purpose of listing the original client operations
		let pendingRevisions = [];
		let bridge = {};
		let lastSyncedRevisionNo = {};
		let storedMessages = {};
		let lastAcknowledgedID;
		let lastInsertedResource;
		let waitingForAck = false;
		let apiManager;
		let framesManager;
		let patcher = new LdPatchParser();
		let processor;
		// Keep a list of loaded channels in case they are referenced in more than
		// one place so we avoid reloading
		let loadedChannels = {};
		let loadedModels = {};
		let lockedGraphs = [];
		let deletedResources = [];
		let integrationPayload;
		let pauseSending = false;

		const EVENT_CONCURRENT_EDIT = 'EVENT_CONCURRENT_EDIT';
		const EVENT_RESOURCE_CREATE = 'EVENT_RESOURCE_CREATE';
		const EVENT_EDIT = 'EVENT_EDIT';

		function guid() {
			return uuidv4();
		}

		this.EVENTS = {
			EVENT_RESOURCE_CREATE,
			EVENT_CONCURRENT_EDIT,
			EVENT_EDIT
		};

		/** We expose certain methods for testing */
		this.test = {
			data: (iri, graphIRI) => { return this.data(iri, graphIRI); },
			processIncomingRevision: processIncomingRevision,
			removeEntailments: removeEntailments,
			computeIRI: (baseIRI, text, useSlashIRI, useUuidIRI, customOptions) => {
				return this.computeIRI(baseIRI, text, useSlashIRI, useUuidIRI, customOptions);
			},
			setChannels: (channelData) => { loadedChannels = channelData; },
			getRevisionNos: () => { return lastSyncedRevisionNo; },
			pauseMessageProcessing: () => { pauseSending = true; },
			resumeMessageProcessing: () => {
				pauseSending = false;
				processPendingRevisions();
			},
			socketEvent: (test) => {
				socket.emit(socketEvent, { test });
			},
			getIntegrationPayload: () => { return integrationPayload; },
			isWaiting: () => { return waitingForAck; },
			setWaiting: () => { waitingForAck = true; },
			setLockedGraph: (graphIRI) => {
				lockedGraphs.push(graphIRI);
				lockAction(lockedGraphs);
			},
			setUnlockedGraph: (graphIRI) => {
				lockedGraphs.splice(lockedGraphs.indexOf(graphIRI), 1);
				lockAction(lockedGraphs);
			},
			lastAcknowledgedID: () => { return lastAcknowledgedID; },
			clearLastAcknowledgedID: () => {
				lastAcknowledgedID = "";
			},
			lastInsertedResource: () => { return lastInsertedResource; },
			getPendingRevisionCount: () => {
				try {
					return pendingRevisions.length;
				}
				catch (e) {
					console.log(e);// eslint-disable-line
				}
			}
		};

		// Simply a handle method for GS code to use for whatever it needs (useful for throwing errors)
		this.handle = function () {
			return true;
		};

		this.createInvitation = async function (username) {
			if (!checkStringParam(username)) throw new Error("Username must be a string.");
			return apiManager.sendData(`/invitation`, {
				subscription: activeSubscription,
				username
			});
		};

		this.respondToInvitation = async function (invitationIRI, status) {
			if (!invitationIRI || invitationIRI.indexOf(INVITATION_NAMESPACE) !== 0) throw new Error("Invitation IRI is invalid.");
			const invitationId = invitationIRI.substring(INVITATION_NAMESPACE.length);
			return apiManager.sendData(`/invitation/` + invitationId + "/response", {
				status
			});
		};

		this.withdrawInvitation = async function (invitationIRI) {
			if (!invitationIRI || invitationIRI.indexOf(INVITATION_NAMESPACE) !== 0) throw new Error("Invitation IRI is invalid.");
			const invitationId = invitationIRI.substring(INVITATION_NAMESPACE.length);
			return apiManager.deleteData(`/invitation/` + invitationId);
		};

		this.createSubscription = async function (name, productID, transferUser) {
			return apiManager.sendData(`/subscription`, {
				name,
				productID,
				transferUser
			});
		};

		this.deleteAccount = async function (userIRI) {
			if (!userIRI || userIRI.indexOf(USER_NAMESPACE) !== 0) throw new Error("User IRI is invalid.");
			const userId = userIRI.substring(USER_NAMESPACE.length);
			return apiManager.deleteData(`/user/${userId}`);
		};

		this.logout = async function () {
			// Logout could potentially be called in lots of places as a safety mechanism so make sure we're actually logged in
			if (!apiManager || !this.getSessionToken()) return;
			const logoutData = await apiManager.sendData(`/authenticate/logout`, {});
			return logoutData.json();
		};

		this.renewSubscription = async function (subscriptionIRI) {
			if (!subscriptionIRI || subscriptionIRI.indexOf(SUBSCRIPTION_NAMESPACE) !== 0) throw new Error("Subscription IRI is invalid.");
			const subscriptionId = subscriptionIRI.substring(SUBSCRIPTION_NAMESPACE.length);
			return apiManager.sendData(`/administration/subscription/` + subscriptionId + "/renew", {});
		};

		this.optimiseSubscription = async function (subscriptionIRI) {
			if (!subscriptionIRI || subscriptionIRI.indexOf(SUBSCRIPTION_NAMESPACE) !== 0) throw new Error("Subscription IRI is invalid.");
			const subscriptionId = subscriptionIRI.substring(SUBSCRIPTION_NAMESPACE.length);
			return apiManager.sendData(`/administration/subscription/` + subscriptionId + "/optimisation", {});
		};

		this.updateSubscription = async function (subscriptionIRI, payload) {
			if (!subscriptionIRI || subscriptionIRI.indexOf(SUBSCRIPTION_NAMESPACE) !== 0) throw new Error("Subscription IRI is invalid.");
			const subscriptionId = subscriptionIRI.substring(SUBSCRIPTION_NAMESPACE.length);
			return apiManager.updateData(`/administration/subscription/` + subscriptionId, payload);
		};

		this.transferSubscription = async function (payload) {
			return apiManager.sendData(`/administration/transfer`, payload);
		};

		this.portSubscription = async function (payload) {
			return apiManager.sendData(`/administration/port`, payload);
		};

		this.diskspaceCheck = async function () {
			const response = await apiManager.getData(`/administration/diskspace`);
			return response.json();
		};

		this.blockSubscription = async function (subscriptionIRI) {
			if (!subscriptionIRI || subscriptionIRI.indexOf(SUBSCRIPTION_NAMESPACE) !== 0) throw new Error("Subscription IRI is invalid.");
			const subscriptionId = subscriptionIRI.substring(SUBSCRIPTION_NAMESPACE.length);
			return apiManager.sendData(`/administration/subscription/` + subscriptionId + "/status",
				{
					status: "blocked"
				});
		};

		this.unblockSubscription = async function (subscriptionIRI) {
			if (!subscriptionIRI || subscriptionIRI.indexOf(SUBSCRIPTION_NAMESPACE) !== 0) throw new Error("Subscription IRI is invalid.");
			const subscriptionId = subscriptionIRI.substring(SUBSCRIPTION_NAMESPACE.length);
			return apiManager.sendData(`/administration/subscription/` + subscriptionId + "/status",
				{
					status: "unblocked"
				});
		};

		this.blockUser = async function (userIRI) {
			if (!userIRI || userIRI.indexOf(USER_NAMESPACE) !== 0) throw new Error("User IRI is invalid.");
			const userId = userIRI.substring(USER_NAMESPACE.length);
			return apiManager.sendData(`/administration/user/` + userId + "/status",
				{
					status: "blocked"
				});
		};

		this.unblockUser = async function (userIRI) {
			if (!userIRI || userIRI.indexOf(USER_NAMESPACE) !== 0) throw new Error("User IRI is invalid.");
			const userId = userIRI.substring(USER_NAMESPACE.length);
			return apiManager.sendData(`/administration/user/` + userId + "/status",
				{
					status: "unblocked"
				});
		};

		this.getUserAsAdmin = async function (userIRI) {
			if (!userIRI || userIRI.indexOf(USER_NAMESPACE) !== 0) throw new Error("User IRI is invalid.");
			const userId = userIRI.substring(USER_NAMESPACE.length);
			const response = await apiManager.getData(`/administration/user/` + userId + "/detail");
			return response.json();
		};

		this.updateUserAsAdmin = async function (userIRI, hasHadTrial) {
			if (!userIRI || userIRI.indexOf(USER_NAMESPACE) !== 0) throw new Error("User IRI is invalid.");
			const userId = userIRI.substring(USER_NAMESPACE.length);
			return apiManager.updateData(`/administration/user/` + userId + "/detail",
				{
					hasHadTrial
				});
		};

		this.getUserSubscriptionsAsAdmin = async function (userIRI) {
			if (!userIRI || userIRI.indexOf(USER_NAMESPACE) !== 0) throw new Error("User IRI is invalid.");
			const userId = userIRI.substring(USER_NAMESPACE.length);
			const response = await apiManager.getData(`/administration/user/` + userId + "/subscription");
			return response.json();
		};

		this.getUserInvitationsAsAdmin = async function (userIRI) {
			if (!userIRI || userIRI.indexOf(USER_NAMESPACE) !== 0) throw new Error("User IRI is invalid.");
			const userId = userIRI.substring(USER_NAMESPACE.length);
			const response = await apiManager.getData(`/administration/user/` + userId + "/invitation");
			return response.json();
		};

		this.getSystemStatistics = async function () {
			const response = await apiManager.getData(`/administration/statistics`);
			return response.json();
		};

		this.updateUser = async function (userIRI, data) {
			if (!userIRI || userIRI.indexOf(USER_NAMESPACE) !== 0) throw new Error("User IRI is invalid.");
			const userId = userIRI.substring(USER_NAMESPACE.length);
			apiManager.updateData("/user/" + userId + "/detail", data);
		};

		this.sendMessage = async function (message) {
			return apiManager.sendData(`/administration/message`, message);
		};

		this.changePassword = async function (userIRI, data) {
			if (!userIRI || userIRI.indexOf(USER_NAMESPACE) !== 0) throw new Error("User IRI is invalid.");
			const userId = userIRI.substring(USER_NAMESPACE.length);
			const response = await apiManager.updateData("/user/" + userId + "/security", data);
			if (response.status === 403) {
				return false;
			}
			return true;
		};

		this.subscriptions = async function (bucket, handleError = false) {
			const errorCodes = handleError ? [504, 503, 502] : [];
			const response = await apiManager.getData(`/subscription`, "application/ld+json", bucket, errorCodes);
			// When it is a new subscription in a new stack might not be up and running fully so might return a 502/503/504;
			if (response.status === 502 || response.status === 503 || response.status === 504) {
				return [];
			}
			return response.json();
		};

		this.subscriptionUsers = async function (projectIRI) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (projectIRI && projectIRI.indexOf(PROJECT_NAMESPACE) !== 0) throw new Error("Project IRI is invalid.");
			const subscriptionId = activeSubscription.substring(SUBSCRIPTION_NAMESPACE.length);
			const projectParams = projectIRI ? "?project=" + projectIRI : "";
			const response = await apiManager.getData(`/subscription/` + subscriptionId + "/user" + projectParams);
			return response.json();
		};

		this.invitees = async function () {
			if (!activeSubscription) throw new NoActiveSubscription();
			const response = await apiManager.sendData(`/invitee`, {
				subscription: activeSubscription
			});
			const json = await response.json();
			return {
				invitations: json.filter(item => item.type === "Invitation"),
				users: json.filter(item => item.type === "User")
			};
		};

		this.invitations = async function () {
			const response = await apiManager.getData(`/invitation`);
			return response.json();
		};

		this.doesUsernameExist = async function (username) {
			if (!checkStringParam(username)) throw new Error("Username must be a string.");
			let apiResponse = await apiManager.sendData("/find/username", { username }, { accept: "application/ld+json" });
			const response = await apiResponse.json();
			return response.exists === true;
		};

		this.isReadOnly = function () {
			return readOnly;
		};

		this.clearActiveSubscription = async function () {
			if (socket) socket.disconnect(true);
			await this.unload();
			activeSubscription = null;
			activeSubscriptionName = "No subscription selected";
			activeSubscriptionBucket = null;
			apiManager.setSubscriptionBucket(null);
			await this.setupSocket();
		};

		this.setActiveSubscription = async function (iri, name, bucket, subscriptionType, setReadOnly = false, options = {
			maxImportSize: 1000000
		}) {
			// We disconnect and reconnect to the socket because the new active subscription may be on a different server
			// to any current active subscription
			if (socket) socket.disconnect(true);
			await this.unload();
			readOnly = setReadOnly;
			activeSubscription = iri;
			activeSubscriptionName = name;
			activeSubscriptionBucket = bucket;
			activeSubscriptionType = subscriptionType;
			if (options.maxImportSize) activeSubscriptionMaxImportSize = options.maxImportSize;
			apiManager.setSubscriptionBucket(bucket);
			await this.setupSocket(bucket);
		};

		this.getActiveSubscription = function () {
			return activeSubscription;
		};

		this.getActiveSubscriptionBucket = function () {
			return activeSubscriptionBucket;
		};

		this.getActiveSubscriptionName = function () {
			return activeSubscriptionName;
		};

		this.getActiveSubscriptionType = function () {
			return activeSubscriptionType;
		};

		this.getActiveSubscriptionMaxImportSize = function () {
			return activeSubscriptionMaxImportSize;
		};

		/**
		 * Generate a UUID for client applicatino to use
		 */
		this.getUUID = function () {
			return guid();
		};

		this.getSessionID = function () {
			return sessionUUID;
		};

		this.getSessionToken = function () {
			return sessionToken;
		};

		this.getEndpoint = function () {
			return GSendpoint;
		};

		this.getFrame = function (frame) {
			if (!frame || frame === "" || frame === undefined || typeof frame !== "string") throw new Error("An IRI must be supplied to retrieve a frame: " + frame);
			return framesManager.getFrame(frame);
		};

		this.isLoaded = function (iri, graphIRI) {
			if (!iri || iri === "" || iri === undefined || typeof iri !== "string") throw new Error("An IRI must be supplied for a load check: " + iri);
			if (!graphIRI || graphIRI === "" || graphIRI === null || graphIRI === undefined || typeof graphIRI !== "string") throw new Error("A graph IRI must be supplied for a loaded check: " + iri);
			const id = graphIRI + "___" + iri;
			return loadedChannels.hasOwnProperty(id);
		};

		this.getClassIRIs = function (iri, graphIRI) {
			if (!iri || iri === "" || iri === undefined || typeof iri !== "string") throw new Error("An IRI must be supplied for a class check: " + iri);
			if (!graphIRI || graphIRI === "" || graphIRI === null || graphIRI === undefined || typeof graphIRI !== "string") throw new Error("A graph IRI must be supplied for a class check: " + iri);
			const id = graphIRI + "___" + iri;
			if (!loadedChannels[id]) throw new Error("Resource not loaded for IRI class check: " + iri);
			if (!loadedChannels[id].type) throw new Error("No type information available for IRI class check: " + iri);
			const classTypes = Array.isArray(loadedChannels[id].type) ? loadedChannels[id].type : [loadedChannels[id].type];
			const classIRIs = classTypes.map(classType => {
				if (classType.startsWith("http")) return classType;
				return internalContext._context.byProperty[classType];
			});
			return classIRIs;
		};

		/**
		 * Get JSON-LD alias for a given IRI
		 */
		this.getAlias = function (iri) {
			return internalContext._context.byIRI[iri] ?
				internalContext._context.byIRI[iri]
				:
				iri;
		};

		this.isLanguageProperty = function () {
			return internalContext._context.byIRI['@language'];
		};

		/**
		 * Get IRI for a given JSON-LD alias
		 */
		this.getIRIfromAlias = function (alias) {
			return internalContext._context.byProperty[alias];
		};

		this.getChannels = function () {
			return loadedChannels;
		};

		this.getBoundObjects = function () {
			return boundObject;
		};

		this.data = function (iri, graphIRI, errorOnNotAvailable = true) {
			if (!iri || iri === "" || iri === undefined || typeof iri !== "string") throw new Error("An IRI must be supplied to retrieve data: " + iri);
			if (!graphIRI || graphIRI === undefined || typeof graphIRI !== "string") throw new Error("A graph IRI must be supplied to retrieve data: " + iri);
			// Make sure we give back just a copy of the data
			const id = graphIRI + "___" + iri;
			if (!loadedChannels[id]) {
				if (errorOnNotAvailable) throw new Error("Unable to retrieve data. The resource details are:\n" + iri + "\n" + graphIRI + "\nPlease ensure you have permissions to access the requested data.");
				return null;
			}
			return cloneDeep(loadedChannels[id]);
		};

		this.isInitialised = function () {
			return isInitialised;
		};

		this.isExiting = function () {
			return isExiting;
		};

		this.initiateExit = function () {
			console.log("Initiating exit ...");// eslint-disable-line
			isExiting = true;
		};

		this.exit = async function () {
			if (socket) socket.disconnect(true);
			await this.unload();
			activeSubscription = null;
			integrationPayload = null;
			sessionUUID = null;
			productInfo = null;
			uploadedFilename = null;
			pendingRevisions = [];
			deletedResources = [];
			lockedGraphs = [];
			cachedData = new Map();
			bridge = {};
			loadedModels = {};
			boundObject = {};
			internalContext = {};
			loadedChannels = {};
			lastSyncedRevisionNo = {};
			unavailableResources = new Set();
			storedMessages = {};
			inFlight = new Map();
			waitingForAck = false;
			isInitialised = false;
			readOnly = false;
			sessionToken = null;
			validation = null;
			chainEntailments = null;
		};

		// Note redirect not used any more
		this.problem = (message, fatal = false, redirect = true, actionable = true, sessionExpired = false) => {// eslint-disable-line
			if (!message || message === "" || message === undefined) throw new Error("A message must be supplied to report a problem.");
			errorReport(message, fatal, actionable, sessionExpired);
		};

		this.initialise = async function (token, endpoint, errorCallback, importCallback, lockCallback, rateLimit = 20) {
			logMessage("Initialising GS");
			if (!token) throw new AuthException("A user must be authenticated to initialise GrafSync.");
			if (endpoint === null || endpoint === undefined) throw new InvalidParameter("No endpoint supplied to GS.");
			await this.exit();
			// In case we are already initialised clean up everything first
			GSendpoint = endpoint;
			sessionUUID = guid();
			sessionToken = token;
			errorReport = errorCallback;
			importAction = importCallback;
			lockAction = lockCallback;
			apiManager = new ApiManager(sessionToken, GSendpoint, (message, sessionExpired) => {
				this.problem(message, true, true, true, sessionExpired);
			}, rateLimit);
			framesManager = new FramesManager(apiManager);
			try {
				await framesManager.initialise();
				processContext();
				processor = new LdPatchProcessor(this.getChannels, this.getBoundObjects, internalContext, patcher, framesManager, processNewData);
				await loadProducts();
				await loadValidation();
				await loadPropertyChains();
			}
			catch (e) {
				return {
					status: false,
					message: e.message
				};
			}
			// Create some helper functions
			let utils = utilities(validation, chainEntailments, this.getChannels, internalContext);
			tp = new TransformProcessor(utils);
			btp = new BridgeTransformProcessor(utils);
			// We need to initialise the socket for non-subscription based messages
			// Once a subscription is selected the messages will go to that subscription bucket's server
			// await this.setupSocket();
			isInitialised = true;
			logMessage("Finished initialising GS");
			return {
				status: true
			};
		};

		function logMessage(message) {
			console.log(message);// eslint-disable-line
		}

		function getDate() {
			const dt = new Date(Date.now());
			return dt.toLocaleString();
		}

		this.setupSocket = async function (bucket) {
			if (!sessionToken) {
				this.problem("Could not initialise socket - no token available.", true);
				return;
			}
			// Websocket does not allow to send the token in the body so it has to go as a query parameter
			// Make sure we force the transport straight to websocket - default is polling
			socket = io(GSendpoint, {
				transports: ['websocket'],
				query: {
					token: sessionToken,
					...(activeSubscription) && { subscription: activeSubscription },
					...(bucket && bucket !== undefined) && { "graphologi-target": bucket }
				}
			});
			if (socket == null) {
				this.problem("Could not initialise socket", true);
				return {
					status: false,
					message: "Socket communication cannot be initiated"
				};
			}
			socket.io.on('error', (error) => {
				logMessage(`Socket IO error.`);
				this.problem(error.toString(), true);
			});
			// If there is an authentication issue we will receive a 'connect_error' event
			socket.on('connect_error', (error) => {
				this.problem(error.toString(), true);
			});
			socket.io.on('reconnect', async () => {
				logMessage(`Reconnection with socket at ... ${getDate()}`);
				let message = [
					"Connection with the server has been re-established."
				];
				if (socketErrorMessage !== "") message.push(socketErrorMessage);
				this.problem(message, false, false);
			});
			socket.io.on('reconnect_attempt', (attemptNumber) => {
				let message = [
					"Communication with the server has been lost.",
					"Trying to reconnect... Attempt " + attemptNumber
				];
				if (socketErrorMessage !== "") message.push(socketErrorMessage);
				this.problem(message, false, false, false);
			});
			socket.io.on('reconnect_error', () => {
				logMessage(`Reconnection error with socket at ... ${getDate()}`);
			});
			socket.io.on('reconnect_failed', () => {
				this.problem(["Communication with the the server has been lost.", "An attempt to reconnect will be made."], true, false, false);
			});
			socket.on('connect_timeout', () => {
				this.problem("Communication with the server has timed out.", true);
			});
			socket.on('disconnect', async (data) => {
				logMessage(`Disconnection from socket at ... ${getDate()}`);
				socketErrorMessage = "";
				if (pendingRevisions.length > 0) socketErrorMessage = "You will lose the last " + pendingRevisions.length + " edits. Please redo those edits once reconnected.";
				// Clear everything out to ensure that the client and server are synced if not via log out
				if (data !== "io client disconnect") {
					logMessage(`Disconnection was not by client.`);
					waitingForAck = false;
					await this.unload();
					this.problem([
						"The network connection has failed.",
						socketErrorMessage,
						"Please log in to continue."], true, false, true, true);
				}
				else {
					logMessage(`Disconnection was by client.`);
				}
			});
			logMessage(`Waiting for socket connection at ... ${getDate()}`);
			await new Promise((resolve) => {
				socket.on('connect', () => {
					// It is only on connect that we get a socket id so we wait for that.
					logMessage("GS initiated socket with id: " + socket.id + " at " + getDate());
					resolve();
				});
			});
			logMessage(`Completing socket event setup at ... ${getDate()}`);
			// Messages about invalid writes will be sent to the client as an 'api' event
			// Basically if we receive a message on this something bad is happening
			socket.on('api', async (apiMessage) => {
				if (apiMessage.message === "Subscription has failed.") {
					this.problem("Unfortunately there has been an issue with processing an operation on this subscription.\nThis subscription will be temporarily suspended whilst support investigates.\nThey will automatically be notified.", true);
				}
				else if (apiMessage.lockedGraphs) {
					lockedGraphs = apiMessage.lockedGraphs;
					lockAction(apiMessage.lockedGraphs);
				}
				else if (apiMessage.reload) {
					logMessage("Server has asked for client to reload at: " + getDate());
					socket.disconnect(true);
					waitingForAck = false;
					await this.unload();
					this.problem("The server you are connected to has changed due to a connection failure. This means we need you to log in again. You may have to redo your last edit.", true);
					return;
				}
				else this.problem(apiMessage.message, true);
			});
			socket.on(socketEvent, (payload) => {
				//const channelForUpdate = payload.channel;
				//logMessage("Hearing message in GS for channel: " + channelForUpdate);
				//logMessage(JSON.stringify(payload, null, 2));
				if (payload.status && payload.status === "error") {
					this.problem(payload.error, true);
				}
				else if (payload.message && payload.message.changeType === "import") {
					// If a client is receiving import message we pause their activity
					// If it not the importing client we throw up a dialog
					const filename = payload.message.filename.indexOf('.ttl') > 0 ?
						payload.message.filename.substring(0, payload.message.filename.length - 4)
						:
						payload.message.filename;
					if (filename !== uploadedFilename) {
						importAction(payload.message.filename);
					}
				}
				else if (payload.message && payload.message.changeType === "batch-operation") {
					if (!lockedGraphs.includes(payload.message.graphIRI)) {
						lockedGraphs.push(payload.message.graphIRI);
						lockAction(lockedGraphs);
					}
				}
				else if (payload.message && payload.message.changeType === "batch-operation-complete") {
					const graphIRI = payload.message.graphIRI;
					if (payload.message.finished === false) {
						this.problem("Batch operation failed.", true);
						return;
					}
					if (payload.message.reset) {
						unavailableResources = new Set();
						loadedModels = {};
					}
					if (lockedGraphs.includes(graphIRI)) {
						lockedGraphs.splice(lockedGraphs.indexOf(graphIRI), 1);
						// Need to clear out loaded resources
						const resources = Object.keys(loadedChannels);
						const resourcesToUnload = resources
							.filter(resource => resource.startsWith(graphIRI + "___"))
							.map(resource => loadedChannels[resource].id);
						this.unload(resourcesToUnload, graphIRI).then(() => {
							// We set a new load key. This can be used by applications to force a rerender
							// which might be necessary because otherwise further message updates might not be heard
							// such as batch operations meaning no lock message
							loadKey = uuidv4();
							lockAction(lockedGraphs, loadKey);
						});
					}
					if (payload.message.name === "import") {
						importCompletionCallback(payload.message);
					}
				}
				else {
					try {
						processIncomingRevision(payload.message, payload.channel, payload.isAdditionalChannel);
					}
					catch (e) {
						console.log("Error processing incoming revision: ", e);// eslint-disable-line
						console.log(JSON.stringify(payload, null, 1));// eslint-disable-line
					}
				}
			});
		};

		function processContext() {
			const globalFrame = framesManager.getFrame();
			internalContext = computeInternalContext(globalFrame);
		}

		function checkStringParam(param) {
			if (!param || param === undefined || param === "" || typeof param !== "string") return false;
			return true;
		}

		this.path = async function (pathPayload) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (typeof pathPayload !== "object") throw new Error("An object must be supplied for path operations.");
			pathPayload.subscription = activeSubscription;
			const response = await apiManager.sendData("/graph/path", pathPayload);
			const data = await response.json();
			return data;
		};

		this.search = async function (searchPayload, uuid) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (typeof searchPayload !== "object") throw new Error("An object must be supplied for search operations.");
			searchPayload.subscription = activeSubscription;
			searchPayload.outputUnd = true;
			const searchResponse = await apiManager.sendData("/search", searchPayload, { accept: "application/ld+json" });
			const searchData = await searchResponse.json();
			if (uuid) searchData.uuid = uuid;
			return searchData;
		};

		this.userSearch = async function (searchPayload) {
			if (typeof searchPayload !== "object") throw new Error("An object must be supplied for search operations.");
			const searchResponse = await apiManager.sendData("/search/user", searchPayload, { accept: "application/ld+json" });
			const searchData = await searchResponse.json();
			return searchData;
		};

		this.autocomplete = async function (acPayload, uuid) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (typeof acPayload !== "object") throw new Error("An object must be supplied for autocomplete operations.");
			acPayload.subscription = activeSubscription;
			let acResponse = await apiManager.sendData("/autocomplete", acPayload, { accept: "application/ld+json" });
			const acData = await acResponse.json();
			if (uuid) acData.uuid = uuid;
			return acData;
		};

		/**
		 * Expecting: value: {IRI}, graph: {graph}
		 */
		this.find = async function (findPayload) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (typeof findPayload !== "object") throw new Error("An object must be supplied for find operations.");
			findPayload.subscription = activeSubscription;
			let findResponse = await apiManager.sendData("/find/iri", findPayload, { accept: "application/ld+json" });
			const findData = await findResponse.json();
			return findData;
		};

		// We minimise the encoding for valid IRIs
		this.encodeRFC3986URIComponent = function (str) {
			const encodedText = str.replace(/[ :/?#[\]@$&+,;=\\|"%^{}~<>`]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
			return encodedText;
		};

		this.computeIRI = function (baseIRI, text, useSlashIRI = true, useUuidIRI = true, customOptions = {}) {
			const iriOptions = {
				prependText: customOptions.prependText || "",
				appendText: customOptions.appendText || "",
				textSeparator: customOptions.textSeparator || "",
				initialLowerCase: customOptions.initialLowerCase || false,
				removeSpaces: customOptions.removeSpaces || false,
				replaceSpaces: customOptions.replaceSpaces || "",
				removeSpecialCharacters: customOptions.removeSpecialCharacters || false,
				replaceSpecialCharacters: customOptions.replaceSpecialCharacters || "",
				minimiseEncoding: customOptions.minimiseEncoding || false
			};
			const slashHash = useSlashIRI ? (baseIRI.endsWith('/') ? '' : '/') : '#';
			text = text.trim();
			if (iriOptions.initialLowerCase) {
				text = text.charAt(0).toLowerCase() + text.substr(1);
			}
			if (iriOptions.removeSpaces) {
				text = text.replace(/\s/g, "");
			}
			else if (iriOptions.replaceSpaces !== "") {
				text = text.replace(/\s/g, iriOptions.replaceSpaces);
			}
			if (iriOptions.removeSpecialCharacters) {
				text = text.replace(/[.,/"£#!$%^&*;:{}=_`~()[\]@\\]/g, "");
			}
			else if (iriOptions.replaceSpecialCharacters !== "") {
				text = text.replace(/[.,/"£#!$%^&*;:{}=_`~()[\]@\\]/g, iriOptions.replaceSpecialCharacters);
			}
			text = text.trim();
			let iriID = useUuidIRI ?
				uuidv4()
				:
				iriOptions.minimiseEncoding ?
					this.encodeRFC3986URIComponent(text)
					:
					encodeURIComponent(text);
			if (!useUuidIRI && iriOptions.prependText !== "") iriID = iriOptions.minimiseEncoding ?
				this.encodeRFC3986URIComponent(iriOptions.prependText) + iriOptions.textSeparator + iriID
				:
				encodeURIComponent(iriOptions.prependText) + iriOptions.textSeparator + iriID;
			if (!useUuidIRI && iriOptions.appendText !== "") iriID = iriID + iriOptions.textSeparator + iriOptions.appendText;
			// Cater for hash in base IRI
			const startIRI = baseIRI.indexOf('#') > 0 ?
				baseIRI.substring(0, baseIRI.indexOf('#'))
				:
				baseIRI;
			const computedIRI = startIRI + slashHash + iriID;
			return computedIRI;
		};

		function checkIRIstatus(id, resolve, reject, time = 30) {
			if (checkingIRI) clearTimeout(checkingIRI);
			checkingIRI = setTimeout(async function () {
				checkingIriAttempts++;
				if (checkingIriAttempts >= 20) {
					checkingIriAttempts = 0;
					reject();
					return;
				}
				const checkPayload = {
					id,
					subscription: activeSubscription
				};
				let response = await apiManager.sendData("/instance/iri/status", checkPayload);
				const checkData = await response.json();
				if ("exists" in checkData && checkData.exists !== "PENDING") {
					checkingIriAttempts = 0;
					resolve(checkData);
					return;
				}
				checkIRIstatus(id, resolve, reject, 100);
			}, time);
		}

		this.waitForIRIcheck = function (id) {
			return new Promise((resolve, reject) => checkIRIstatus(id, resolve, reject));
		};

		this.reserveIRI = async function (iri, graph, classIRI) {
			if (!activeSubscription) throw new NoActiveSubscription();
			// Don't allow two checks at once
			if (checkingIriAttempts > 0) return;
			const iriPayload = {
				iri,
				graph,
				classIRI,
				subscription: activeSubscription
			};
			let response = await apiManager.sendData("/instance/iri", iriPayload);
			const iriData = await response.json();
			if (iriData.id) {
				try {
					const existsInfo = await this.waitForIRIcheck(iriData.id);
					return {
						exists: existsInfo.exists,
						id: existsInfo.id
					};
				}
				catch (e) {
					throw new Error("Cannot complete IRI check.");
				}
			}
			else {
				return iriData;
			}
		};

		this.checkForInbound = async function (checkPayload) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (typeof checkPayload !== "object") throw new Error("An object must be supplied for check operations.");
			checkPayload.subscription = activeSubscription;
			let findResponse = await apiManager.sendData("/check", checkPayload, { accept: "application/ld+json" });
			const findData = await findResponse.json();
			return findData.exists === true;
		};

		this.history = async function (iri, graph, path, isReversed = false, startDate, endDate, users, maxItems = 100, offset = 0) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!checkStringParam(iri) || !checkStringParam(graph))
				throw new Error("A resource IRI and graph IRI must be supplied for a history operation.");
			const historyResponse = await apiManager.sendData("/history", {
				iri,
				graph,
				...(startDate) && { startDate },
				...(endDate) && { endDate },
				...(users) && { users },
				isReversed,
				...(path) && { path },
				subscription: activeSubscription,
				maxItems,
				offset
			}, { accept: "application/json" });
			const historyData = await historyResponse.json();
			return historyData;
		};

		this.statusTimeline = async function (iri, graph) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!checkStringParam(iri) || !checkStringParam(graph))
				throw new Error("A resource IRI and graph IRI must be supplied for a history operation.");
			const historyResponse = await apiManager.sendData("/history/state", {
				iri,
				graph,
				subscription: activeSubscription
			}, { accept: "application/json" });
			const historyData = await historyResponse.json();
			return historyData;
		};

		this.downloadHistory = async function (iri, graph, path, isReversed = false, startDate, endDate, users, maxItems = 100, offset = 0, format) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!checkStringParam(iri) || !checkStringParam(graph))
				throw new Error("A resource IRI and graph IRI must be supplied for a history operation.");
			const payload = {
				iri,
				graph,
				...(startDate) && { startDate },
				...(endDate) && { endDate },
				...(users) && { users },
				isReversed,
				...(path) && { path },
				subscription: activeSubscription,
				maxItems,
				offset
			};
			const historyResponse = await apiManager.sendData("/history", payload, { accept: format || "application/json" });
			const historyData = await historyResponse.blob();
			const url = window.URL.createObjectURL(historyData);
			const link = document.createElement('a');
			link.href = url;
			let fileExtension = format === "text/tab-separated-values" ? ".tsv" : ".json";
			link.setAttribute('download', iri.replace(/[^a-z0-9]/gi, '_').toLowerCase() + "_history" + fileExtension);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		};

		this.download = async function (name, iri, graph, format = "application/ld+json", options = [], embed = false) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!checkStringParam(name) || !checkStringParam(iri) || !checkStringParam(format) || !checkStringParam(graph))
				throw new InvalidParameter("A name, IRI, format and language must be supplied for a download.");
			const downloadUrl = `/view/${name}`;
			const response = await apiManager.sendData(downloadUrl,
				{
					graph,
					options,
					embed,
					outputUnd: false,
					subscription: activeSubscription,
					iri
				}, { accept: format });
			const data = await response.blob();
			const url = window.URL.createObjectURL(data);
			const link = document.createElement('a');
			link.href = url;
			let fileExtension = ".jsonld";
			if (format === "text/turtle") fileExtension = ".ttl";
			if (format === "application/rdf+xml") fileExtension = ".xml";
			if (format === "application/n-triples") fileExtension = ".nt";
			link.setAttribute('download', iri.replace(/[^a-z0-9]/gi, '_').toLowerCase() + fileExtension);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		};

		this.cache = function (name, data) {
			cachedData.set(name, {
				time: Date.now(),
				data
			});
		};

		this.retrieveCache = function (name) {
			return cachedData.get(name);
		};

		this.clearCache = function (name) {
			cachedData.delete(name);
		};

		this.export = async function (name, iri, graph, format = "application/ld+json", language, propertiesToInclude = []) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!checkStringParam(name) || !checkStringParam(iri) || !checkStringParam(format) || !checkStringParam(graph))
				throw new Error("A name, IRI, format and graph must be supplied for an export.");
			const url = `/view/${name}`;
			const response = await apiManager.sendData(url,
				{
					graph,
					subscription: activeSubscription,
					outputUnd: true,
					...(language) && { language: Array.isArray(language) ? language : [language] },
					iri,
					...(propertiesToInclude.length > 0) && { predicates: propertiesToInclude }
				}, { accept: format });
			if (format === "application/ld+json") return await response.json();
			await response.text();
		};

		// name, graph, resource, action, additionalResources, resourceList, value
		this.operate = async function (name, graph, parameters) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!checkStringParam(name) || !checkStringParam(graph))
				throw new Error("A name and graph must be supplied for an operation.");
			const url = `/project/operation/${name}`;
			return apiManager.sendData(url,
				{
					socketId: socket.id,
					graph,
					parameters,
					subscription: activeSubscription
				});
		};

		this.operationSummary = async function (name, graph, parameters) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!checkStringParam(name) || !checkStringParam(graph))
				throw new Error("A name and graph must be supplied for an operation summary.");
			const url = `/project/operation/${name}/summary`;
			return apiManager.sendData(url,
				{
					socketId: socket.id,
					graph,
					parameters,
					subscription: activeSubscription
				});
		};

		this.sendFeedback = async function (file, feedback) {
			if (!checkStringParam(feedback) || feedback === "") throw InvalidParameter("Feedback must be a string.");
			const formData = new FormData();
			if (file) formData.append('file', file);
			formData.append('feedback', feedback);
			let uploadResponse = await apiManager.uploadFeedback(formData);
			if (uploadResponse.status === 415) {
				throw new Error(uploadResponse.statusText);
			}
			const uploadData = await uploadResponse.json();
			return uploadData;
		};

		this.setMaintenanceWindow = async function (startTime, endTime) {
			if (endTime < startTime) throw InvalidParameter("End time must be greate than start time.");
			if (startTime !== 0 && startTime < Date.now()) throw InvalidParameter("Start time must be greater than the current time.");
			return apiManager.sendData(`/administration/maintenance`, {
				startTime,
				endTime
			});
		};

		this.uploadProject = async function (file, projectType, title, description) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!checkStringParam(projectType))
				throw new InvalidParameter("A project type must be supplied to upload a project.");
			const formData = new FormData();
			formData.append('file', file);
			const projectMetadata = {
				...(title) && { title },
				...(description) && { description }
			};
			formData.append('projectMetadata', JSON.stringify(projectMetadata));
			let uploadResponse = await apiManager.uploadProject(formData, activeSubscription, projectType);
			if (uploadResponse.status === 415) {
				throw new Error(uploadResponse.statusText);
			}
			const uploadData = await uploadResponse.json();
			uploadedFilename = uploadData.filename;
			return uploadData;
		};

		this.uploadExampleProject = async function (id) {
			if (!activeSubscription) throw new NoActiveSubscription();
			let uploadResponse = await apiManager.uploadExampleProject(id, activeSubscription);
			if (uploadResponse.status === 415) {
				throw new Error(uploadResponse.statusText);
			}
			const uploadData = await uploadResponse.json();
			uploadedFilename = uploadData.filename;
			return uploadData;
		};

		this.upload = async function (file, projectIRI, graphIRI, options, mapping, importContext, completionCallback) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!file || !checkStringParam(projectIRI) || !checkStringParam(graphIRI))
				throw new InvalidParameter("A file, project and graph must be supplied for an upload.");
			const formData = new FormData();
			formData.append('file', file);
			formData.append('options', JSON.stringify(options));
			if (mapping && Object.keys(mapping).length > 0) {
				formData.append('mapping', JSON.stringify(mapping));
			}
			if (importContext) {
				formData.append('importContext', JSON.stringify(importContext));
			}
			importCompletionCallback = completionCallback;
			let uploadResponse = await apiManager.uploadData(formData, projectIRI, graphIRI, activeSubscription);
			if (uploadResponse.status === 415) {
				throw new Error(uploadResponse.statusText);
			}
			const uploadData = await uploadResponse.json();
			uploadedFilename = uploadData.filename;
			return uploadData;
		};

		this.uploadStatus = async function (id) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!checkStringParam(id)) throw new InvalidParameter("An id must be supplied to check upload status.");
			let uploadStatusResponse = await apiManager.uploadStatus(id, activeSubscription);
			const uploadStatusData = await uploadStatusResponse.json();
			return uploadStatusData;
		};

		this.sparql = async function (graphIRI, query) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!checkStringParam(graphIRI)) throw new InvalidParameter("A graph must be supplied for a query.");
			const subscriptionId = activeSubscription.substring(SUBSCRIPTION_NAMESPACE.length);
			const payload = new URLSearchParams({
				'named-graph-uri': graphIRI,
				'query': query
			});
			let queryResponse = await apiManager.sparql("/subscription/" + subscriptionId + "/sparql", payload);
			if (queryResponse.status === 415) {
				throw new Error(queryResponse.statusText);
			}
			const queryData = await queryResponse.json();
			return queryData;
		};

		this.sparqlProject = async function (graphIRI, query, queryProject = []) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!checkStringParam(graphIRI)) throw new InvalidParameter("A graph must be supplied for a query.");
			const subscriptionId = activeSubscription.substring(SUBSCRIPTION_NAMESPACE.length);
			const graphList = [graphIRI, ...queryProject];
			let formData = new FormData();
			formData.append('query', query);
			graphList.forEach(graph => formData.append('named-graph-uri', graph));
			const payload = new URLSearchParams(formData);
			let queryResponse = await apiManager.sparql("/subscription/" + subscriptionId + "/project/sparql", payload);
			if (queryResponse.status === 415) {
				throw new Error(queryResponse.statusText);
			}
			const queryData = await queryResponse.json();
			return queryData;
		};

		this.takeSnapshot = async function (configurationIRI, payload) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!configurationIRI || configurationIRI.indexOf(SNAPSHOT_CONFIGURATION_NAMESPACE) !== 0) throw new Error("Configuration IRI is invalid.");
			return apiManager.sendData("/snapshot", {
				snapshotConfiguration: configurationIRI,
				subscription: activeSubscription,
				...(payload.name) && { name: payload.name },
				...(payload.comment) && { comment: payload.comment },
				isVersion: payload.isVersion === true
			});
		};

		this.createSnapshotConfiguration = async function (projectIRI) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!projectIRI || projectIRI.indexOf(PROJECT_NAMESPACE) !== 0) throw new Error("Project IRI is invalid.");
			return apiManager.sendData("/snapshotconfiguration", {
				project: projectIRI,
				subscription: activeSubscription
			});
		};

		this.deleteSnapshotConfiguration = async function (configurationIRI) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!configurationIRI || configurationIRI.indexOf(SNAPSHOT_CONFIGURATION_NAMESPACE) !== 0) throw new Error("Configuration IRI is invalid.");
			return apiManager.sendData("/snapshotconfiguration/removal", {
				subscription: activeSubscription,
				snapshotConfiguration: configurationIRI
			});
		};

		this.updateSnapshotConfiguration = async function (configurationIRI, data) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!configurationIRI || configurationIRI.indexOf(SNAPSHOT_CONFIGURATION_NAMESPACE) !== 0) throw new Error("Configuration IRI is invalid.");
			data.subscription = activeSubscription;
			data.snapshotConfiguration = configurationIRI;
			apiManager.updateData("/snapshotconfiguration", data);
		};

		this.snapshotConfigurations = async function () {
			if (!activeSubscription) throw new NoActiveSubscription();
			const subscriptionId = activeSubscription.substring(SUBSCRIPTION_NAMESPACE.length);
			const response = await apiManager.getData("/subscription/" + subscriptionId + "/snapshotconfiguration");
			const configurations = await response.json();
			return configurations;
		};

		this.createIntegration = async function (data) {
			const { name, endpoint, format, changesOnly } = data;
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!checkStringParam(name)) throw new Error("Name must be a string.");
			if (!checkStringParam(endpoint)) throw new Error("Endpoint must be a string.");
			if (!checkStringParam(format)) throw new Error("Format must be a valid media type.");
			return apiManager.sendData("/integration", {
				name,
				subscription: activeSubscription,
				endpoint,
				changesOnly,
				format
			});
		};

		this.deleteIntegration = async function (integrationIRI) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!integrationIRI || integrationIRI.indexOf(INTEGRATION_NAMESPACE) !== 0) throw new Error("Integration IRI is invalid.");
			return apiManager.sendData("/integration/removal", {
				subscription: activeSubscription,
				integration: integrationIRI
			});
		};

		this.updateIntegration = async function (integrationIRI, data) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!integrationIRI || integrationIRI.indexOf(INTEGRATION_NAMESPACE) !== 0) throw new Error("Integration IRI is invalid.");
			data.integration = integrationIRI;
			data.subscription = activeSubscription;
			apiManager.updateData("/integration", data);
		};

		this.integrations = async function () {
			if (!activeSubscription) throw new NoActiveSubscription();
			const subscriptionId = activeSubscription.substring(SUBSCRIPTION_NAMESPACE.length);
			const response = await apiManager.getData("/subscription/" + subscriptionId + "/integration");
			const integrations = await response.json();
			return integrations;
		};

		this.prefixes = async function () {
			if (!activeSubscription) throw new NoActiveSubscription();
			const subscriptionId = activeSubscription.substring(SUBSCRIPTION_NAMESPACE.length);
			const response = await apiManager.getData("/subscription/" + subscriptionId + "/prefix", "application/json");
			const prefixes = await response.json();
			return prefixes;
		};

		this.addPrefix = async function (prefix, iri) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!checkStringParam(prefix)) throw new Error("Prefix must be a string.");
			if (!checkStringParam(iri)) throw new Error("IRI must be a string.");
			const subscriptionId = activeSubscription.substring(SUBSCRIPTION_NAMESPACE.length);
			return apiManager.sendData("/subscription/" + subscriptionId + "/prefix", {
				prefix,
				iri
			});
		};

		this.doGetRequest = async function (url, uuid) {
			const response = await apiManager.get(url);
			const responseData = await response.json();
			if (uuid) responseData.uuid = uuid;
			return responseData;
		};

		this.synchroniseChanges = async function (integrationIRI, graphIRI) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!checkStringParam(integrationIRI) || !checkStringParam(graphIRI))
				throw new InvalidParameter("An integration and a graph must be supplied for to get change information.");
			const response = await apiManager.sendData("/integration/changedetail", {
				subscription: activeSubscription,
				integration: integrationIRI,
				graph: graphIRI
			});
			const changes = response.json();
			return changes;
		};

		this.synchroniseDeletions = async function (integrationIRI, graphIRI) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!checkStringParam(integrationIRI) || !checkStringParam(graphIRI))
				throw new InvalidParameter("An integration and a graph must be supplied for to get change information.");
			const response = await apiManager.sendData("/integration/deletiondetail", {
				subscription: activeSubscription,
				integration: integrationIRI,
				graph: graphIRI
			});
			const changes = response.json();
			return changes;
		};

		this.synchronise = async function (integrationIRI, graphIRI, iris) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!iris || !checkStringParam(integrationIRI) || !checkStringParam(graphIRI))
				throw new InvalidParameter("An integration, list of components and a graph must be supplied for an integration.");
			const response = await apiManager.sendData("/integration/synchronisation", {
				subscription: activeSubscription,
				integration: integrationIRI,
				graph: graphIRI,
				iri: iris
			});
			const headers = response.headers;
			if (headers.has("Content-Type")) {
				const payload = await response.json();
				integrationPayload = payload;
			}
		};

		async function loadProducts() {
			const response = await apiManager.getData("/system/product");
			productInfo = await response.json();
		}

		this.subscriptionOptions = function () {
			return [...productInfo.subscriptions];
		};

		this.applicationTitle = function () {
			return productInfo.title;
		};

		async function loadValidation() {
			const response = await apiManager.getData("/system/validation");
			validation = await response.json();
		}

		async function loadPropertyChains() {
			const response = await apiManager.getData("/system/propertychain");
			chainEntailments = await response.json();
		}

		// Allows an application to check whether a value exists or not for a resource
		this.doesValueExist = function (iri, graph, property, index = null, lang = null, value, datatype, propertyGraphIRI) {
			if (!iri || !graph || !property) throw new InvalidParameter("An id an property must be supplied for a value existence check.");
			const id = graph + "___" + iri;
			try {
				const data = loadedChannels[id];
				if (property in data === false) {
					return false;
				}
				this.checkIfAliasOrIRI(property, propertyGraphIRI);
				let foundValue = getPropertyValueText(iri, graph, property, index, lang, value, datatype);
				const containerType = internalContext._context.propertyContainer[property];
				// Where a property is not defined as a set or language and no index is supplied
				// but can contain potentially have multiple values
				// getPropertyValueText will return true if there is a value already
				// We need a further check here to check that the existing value is the same as supplied
				if (containerType !== '@set' && containerType !== '@language'
					&& datatype !== "http://www.w3.org/2001/XMLSchema#boolean"
					&& index === null && foundValue) {
					if (data[property] !== value) foundValue = false;
				}
				// Similar can happen for language containers
				else if (lang && containerType === '@language' && foundValue) {
					if (data[property][lang] !== value) foundValue = false;
				}
				return foundValue;
			}
			catch (e) {
				// So if the value doesnt exist getPropertyValueText might throw an error
				// We make the assumption that this means the value does not exist
				return false;
			}
		};

		function getPropertyValueText(iri, graph, property, index = null, lang, value, datatype) {
			const id = graph + "___" + iri;
			const data = loadedChannels[id];
			const valueProp = internalContext._context.byIRI['@value'];
			const datatypeProp = internalContext._context.byIRI['@type'];
			const languageProp = internalContext._context.byIRI['@language'];
			const propertyType = internalContext._context.propertyType[property];
			const containerType = internalContext._context.propertyContainer[property];
			let valueText = "";
			let languageText = "";
			let datatypeText = "";
			let isIRI = false;

			// Check not overridden locally
			let dataItem = null;
			if (index === null || typeof index === "undefined" || (!Array.isArray(data[property]) && containerType !== '@language')) {
				// If this is a language container the form will be 'en': {'word'} (without an array of values) if only one value
				if (lang && containerType === '@language') {
					if (data[property]) {
						if (Array.isArray(data[property][lang])) {
							dataItem = data[property][lang].find(existingValue => value === existingValue);
							if (!dataItem) throw new Error("Trying to alter a value that does not exist: " + value);
						}
						else {
							dataItem = data[property][lang];
							if (!dataItem) throw new Error("Trying to alter a language that does not exist: " + lang);
						}
					}
				}
				else if (containerType === '@set' || Array.isArray(data[property])) {
					if (Array.isArray(data[property])) {
						dataItem = data[property].find(existingValue => {
							if (typeof existingValue === 'object') {
								// == used here because of datatype issues
								if (existingValue["@value"] == value && existingValue[datatypeProp] === datatype) {
									return true;
								}
								if (existingValue["@value"] === value && existingValue[languageProp] === lang) {
									return true;
								}
								// Where an IRI is on a property that isn't in a context it will be an object
								// with just an id property
								if (existingValue.id && existingValue.id === value) return true;
								return false;
							}
							// If it is a native JSON number and we're upating with a number that's okay
							else if (typeof existingValue === 'number'
								&& datatype === integerXSDtype && parseInt(value) === existingValue) {
								return true;
							}
							else if (typeof existingValue === 'boolean'
								&& datatype === booleanXSDtype && (value === "true") === existingValue) {
								return true;
							}
							// Integers may be stored as strings in JSON but type coercion still forces them to integers
							else if (propertyType === datatype && existingValue == value) return value;
							// Otherwise apart from strings if the datatype is different can't be the same
							else if (datatype && propertyType !== datatype && datatype !== "http://www.w3.org/2001/XMLSchema#string") return false;
							return value === existingValue;
						});
						if (dataItem === null) throw new Error("Trying to alter a value that does not exist");
					}
					else {
						dataItem = data[property];
						if (!dataItem) throw new Error("Trying to alter a value that does not exist");
					}
				}
				else {
					dataItem = data[property];
				}
			}
			else if (lang && containerType === '@language') {
				if (Array.isArray(data[property][lang]) && index !== null) {
					dataItem = data[property][lang][index];
				}
				else {
					dataItem = data[property][lang];
				}
			}
			else {
				dataItem = data[property][index];
			}
			if (dataItem !== null) {
				if (dataItem['@value']) {
					valueText = dataItem['@value'];
				}
				else if (dataItem[valueProp]) {
					valueText = dataItem[valueProp];
				}
				else if (typeof dataItem === "object" && dataItem.id) {
					valueText = dataItem.id;
					isIRI = true;
				}
				else {
					valueText = dataItem;
				}
				if (lang) {
					languageText = "@" + lang;
				} else if (dataItem['@language']) {
					languageText = "@" + dataItem['@language'];
				} else if (dataItem[languageProp]) {
					languageText = "@" + dataItem[languageProp];
				}
				else {
					if (dataItem['@type']) {
						if (dataItem['@type'] !== '@id') {
							datatypeText = "^^<" + dataItem['@type'] + ">";
						}
						else isIRI = true;
					}
					else if (dataItem[datatypeProp]) {
						if (dataItem[datatypeProp] !== '@id') {
							datatypeText = "^^<" + dataItem[datatypeProp] + ">";
						}
						else isIRI = true;
					}
					else if (propertyType !== "@id" && propertyType !== null && propertyType !== undefined) {
						datatypeText += "^^<" + propertyType + ">";
					}
					else if (propertyType === "@id") {
						// We need to convert type aliases to IRIs
						if (!valueText.startsWith("http") && !valueText.startsWith("urn:")) {
							const propertyIRI = internalContext._context.byProperty[valueText];
							if (propertyIRI) valueText = propertyIRI;
						}
						isIRI = true;
					}
					else if (typeof valueText === "number") {
						// Check for native JSON type coercion to integer or decimal
						if (valueText.toString().indexOf(".") > 0) {
							datatypeText = "^^<" + decimalXSDtype + ">";
						}
						else datatypeText = "^^<" + integerXSDtype + ">";
					}
					else if (typeof valueText === "boolean") {
						// Check for native JSON type coercion to boolean
						datatypeText = "^^<" + booleanXSDtype + ">";
					}
				}
				if (isIRI) {
					valueText = '<' + valueText + '>';
				}
				else {
					valueText = '"' + escapeText(valueText) + '"';
				}
				return valueText + languageText + datatypeText;
			}
			else {
				throw new Error("Cannot compute item being updated");
			}
		}

		function escapeText(text) {
			if (typeof text !== "string") return text;
			const regexBackSlash = /\\/g;
			const regexTab = /\t/g;
			const regexQuote = /"/g;
			text = text.replace(regexBackSlash, "\\\\");
			text = text.replace(regexTab, "\\t");
			text = text.replace(regexQuote, "\\u0022");
			return text;
		}

		this.getIRIsForProperty = function (data, property) {
			if (typeof data[property] === "object" && !Array.isArray(data[property])) {
				return [data[property].id];
			}
			else if (Array.isArray(data[property])) {
				return data[property].map(item => {
					if (typeof item === "object") {
						return item.id;
					}
					else return item;
				});
			}
			else if (data[property]) {
				return [data[property]];
			}
			return [];
		};

		// If there is no alias we compute the equivalent information from the ontology data for the resource
		this.checkIfAliasOrIRI = (value, graphIRI) => {
			const propertyIRI = internalContext._context.byProperty[value];
			// We return only if an alias. If not an alias the definition may vary between graphs
			// So we recompute on the fly each time
			if (propertyIRI && !value.startsWith("https://") && !value.startsWith("http://")) return propertyIRI;
			if (!graphIRI) throw new InvalidParameter("For an unaliased property a property graph must be supplied. " + value + " " + graphIRI);
			// If an IRI that isn't listed in the context index add it
			if (value.startsWith("https://") || value.startsWith("http://")) {
				const id = graphIRI + "___" + value;
				internalContext._context.byProperty[value] = value;
				internalContext._context.byIRI[value] = value;
				if (internalContext._context.propertyType[value]) delete internalContext._context.propertyType[value];
				if (loadedChannels.hasOwnProperty(id)) {
					const propertyData = this.data(value, graphIRI);
					if (propertyData.type.includes("ObjectProperty")) {
						internalContext._context.propertyType[value] = "@id";
					}
					else if (propertyData.range) {
						// We don't record the datatype because JSON-LD serialisation won't know about it so we 
						// use default JSON-LD approach of @value and type props
						// We take the first datatype for now
						//internalContext._context.propertyType[value] = propertyData.range[0];
					}
				}
				else {
					// Here we could interpret the same we are in the patch processor by using supplied datatype if a literal or IRI
					throw new Error("Cannot check an unloaded IRI");
				}
				return value;
			}
			return null;
		};

		// lang is optional, as is value, but value needs to be supplied for multi-valued properties
		this.deleteProperty = function (iri, graph, property, value, lang, datatype, propertyGraphIRI = null) {
			if (!activeSubscription) throw new NoActiveSubscription();
			const id = graph + "___" + iri;
			const bo = boundObject[id];
			if (bo === undefined) {
				throw new InvalidUpdateOperationException("Invalid IRI supplied for delete operation: " + id);
			}
			this.checkIfAliasOrIRI(property, propertyGraphIRI);
			const propertyIRI = internalContext._context.byProperty[property];
			if (propertyIRI == null) {
				throw new PropertyNotAvailableException("Attempting to delete using an invalid property: " + property);
			}
			if (!isPropertyLoaded(iri, graph, propertyIRI)) {
				throw new InvalidUpdateOperationException("Cannot delete a property that is not loaded: " + property);
			}
			if (value === "") {
				throw new InvalidUpdateOperationException("Empty values are not permitted for delete operations");
			}
			const containerType = internalContext._context.propertyContainer[property];
			if (containerType === '@list') {
				throw new InvalidUpdateOperationException("It is not possible to use deleteProperty on a property defined as a list");
			}
			let patchText = '<' + bo._iri + '> <' + propertyIRI + '> ';
			patchText += getPropertyValueText(iri, graph, property, undefined, lang, value, datatype);
			createDeletePatch(patchText, bo._iri, graph, propertyGraphIRI);
		};

		this.deleteTriple = function (iri, graph, property, value, lang, datatype) {
			if (!activeSubscription) throw new NoActiveSubscription();
			const id = graph + "___" + iri;
			const bo = boundObject[id];
			if (bo === undefined) {
				throw new InvalidUpdateOperationException("Invalid IRI supplied for delete operation: " + id);
			}
			if (value === "") {
				throw new InvalidUpdateOperationException("Empty values are not permitted for delete operations");
			}
			const alias = this.getAlias(property);
			// We return only if an alias. If not an alias the definition may vary between graphs
			// So we recompute on the fly each time
			if (alias && !alias.startsWith("https://") && !alias.startsWith("http://")) {
				throw new InvalidUpdateOperationException("Cannot remove a triple for an aliased property");
			}
			let patchText = '<' + bo._iri + '> <' + property + '> ';
			patchText += getPropertyValueText(iri, graph, property, undefined, lang, value, datatype);
			createDeletePatch(patchText, bo._iri, graph);
		};
		
		this.replace = function (iri, graph, propertyToReplace, valueToReplace, langToReplace, datatypeToReplace, property, value, lang, datatype) {
			if (!activeSubscription) throw new NoActiveSubscription();
			const id = graph + "___" + iri;
			const bo = boundObject[id];
			if (bo === undefined) {
				throw new InvalidUpdateOperationException("Invalid IRI supplied for replace operation: " + id);
			}
			//if (loadedChannels[id].pendingDeletion) throw new InvalidUpdateOperationException("Attempting to update a deleted resource");
			const propertyToReplaceIRI = internalContext._context.byProperty[propertyToReplace];
			if (propertyToReplaceIRI == null) {
				throw new PropertyNotAvailableException("Attempting to replace using an invalid property: " + propertyToReplace);
			}
			if (!isPropertyLoaded(iri, graph, propertyToReplaceIRI)) {
				throw new InvalidUpdateOperationException("Cannot replace a property that is not loaded: " + propertyToReplace);
			}
			const propertyIRI = internalContext._context.byProperty[property];
			if (propertyIRI == null) {
				throw new PropertyNotAvailableException("Attempting to replace with an invalid property: " + property);
			}
			if (value === "") {
				throw new InvalidUpdateOperationException("Empty values are not permitted for replace operations");
			}
			const containerType = internalContext._context.propertyContainer[property];
			if (containerType === '@list') {
				throw new InvalidUpdateOperationException("It is not possible to use replaceProperty on a property defined as a list");
			}
			const propertyType = internalContext._context.propertyType[property];
			let patchText = '<' + bo._iri + '> <' + propertyToReplaceIRI + '> ';
			patchText += getPropertyValueText(iri, graph, propertyToReplace, undefined, langToReplace, valueToReplace, datatypeToReplace);
			patchText += '<' + propertyIRI + '> ';
			// Replacement value
			if (propertyType === "@id") {
				patchText += '<' + value + ">";
			}
			else {
				patchText += '"' + escapeText(value) + "\"";
				// Need a check here because the language may be defined in the context
				if (lang && typeof lang !== "undefined" && lang !== "") {
					patchText += "@" + lang;
				}
				// Check if datatype defined in context or if a datatype supplied
				// If supplied that overrided the context datatype, but we would not expect that to happen
				// Really the datatype would only supplied on odd occassions where it is not possible to define in context
				// skos:notation is a good example of potential varying datatypes
				if (propertyType != null || datatype != null) {
					let updateType = propertyType;
					if (datatype) updateType = datatype;
					patchText += "^^<" + updateType + ">";
				}
			}
			createReplacePatch(patchText, bo._iri, graph);
		};

		// If index is supplied it is the nth item in a set
		this.updateProperty = function (iri, graph, property, value, lang, index, propertyGraphIRI = null) {
			if (!activeSubscription) throw new NoActiveSubscription();
			const id = graph + "___" + iri;
			const bo = boundObject[id];
			if (bo === undefined) {
				throw new InvalidUpdateOperationException("Invalid IRI supplied for update operation: " + id);
			}
			//if (loadedChannels[id].pendingDeletion) throw new InvalidUpdateOperationException("Attempting to update a deleted resource");
			if (typeof value == "undefined") {
				throw new InvalidUpdateOperationException("Invalid update value for update operation");
			}
			if (value === "") {
				throw new InvalidUpdateOperationException("Empty values are not permitted for update operations");
			}
			this.checkIfAliasOrIRI(property, propertyGraphIRI);
			const propertyIRI = internalContext._context.byProperty[property];
			if (propertyIRI == null) {
				throw new PropertyNotAvailableException("Attempting to update using an invalid property: " + property);
			}
			if (!isPropertyLoaded(iri, graph, propertyIRI)) {
				throw new InvalidUpdateOperationException("Cannot update a property that is not loaded: " + property);
			}
			const containerType = internalContext._context.propertyContainer[property];
			if (containerType === '@list') {
				throw new InvalidUpdateOperationException("It is not possible to use updateProperty on a property defined as a list");
			}
			if (containerType === '@set' && index === null) {
				throw new InvalidUpdateOperationException("A property defined as set must provide an index value");
			}
			let patchText = '<' + bo._iri + '> <' + propertyIRI + '> ';
			let objectText = getPropertyValueText(iri, graph, property, index, lang);
			patchText += objectText;
			// Replacement value
			let propertyType = internalContext._context.propertyType[property];
			if (propertyType === "@id") {
				patchText += '<' + value + ">";
			}
			else {
				patchText += ' "' + escapeText(value) + "\"";
			}
			// Need a check here because the language may be defined in the context
			if (lang && typeof lang !== "undefined" && lang !== "") {
				patchText += "@" + lang;
			}
			else {
				if (propertyType !== "@id") {
					// Check if datatype defined in context
					if (propertyType != null) {
						patchText += "^^<" + propertyType + ">";
					}
					// If there is no datatype defined in context then make assumption that value replacing has same datatype as literal
					else if (objectText.indexOf('"^^') > -1) {
						patchText += objectText.substring(objectText.indexOf('"^^') + 1);
					}
				}
			}
			createUpdatePatch(patchText, bo._iri, graph, propertyGraphIRI);
		};

		// We actually need to allow a client to optionally control the datatype because it is not always known from the JSON-LD
		// We expect only one of lang OR datatype as both cannot occur together
		// propertyGraphIRI can be used if the property to be added is not aliased and is from a different named graph to the resource
		// If it's a property with no range we won't know if an IRI is being supplied so application needs to tell us
		this.addProperty = function (iri, graph, property, value, lang = null, datatype = null, propertyGraphIRI = null, suppliedPropertyType) {
			if (!activeSubscription) throw new NoActiveSubscription();
			const id = graph + "___" + iri;
			const bo = boundObject[id];
			if (bo === undefined) {
				throw new InvalidUpdateOperationException("Invalid IRI supplied for add operation: " + id);
			}
			if (value === "") {
				throw new InvalidUpdateOperationException("Empty values are not permitted for add operations");
			}
			if (lang && datatype) {
				throw new InvalidUpdateOperationException("It is not valid to add a property with both language and datatype");
			}
			this.checkIfAliasOrIRI(property, propertyGraphIRI);
			const propertyIRI = internalContext._context.byProperty[property];
			if (propertyIRI == null) {
				throw new PropertyNotAvailableException("Attempting to add an invalid property: " + property);
			}
			if (!isPropertyLoaded(iri, graph, propertyIRI)) {
				throw new InvalidUpdateOperationException("Cannot add a property that is not loaded: " + property);
			}
			const containerType = internalContext._context.propertyContainer[property];
			if (containerType === '@list') {
				throw new InvalidUpdateOperationException("It is not possible to use addProperty on a property defined as a list");
			}

			const exists = this.doesValueExist(iri, graph, property, null, lang, value, datatype, propertyGraphIRI);
			// Basically this means another application has beaten the local client to adding this triple
			// This might happen if, say, search results are being presented to a user to select one
			// By the time they select the result somebody else might have added it
			if (exists) {
				return;
			}

			// If an aliased type get the IRI for it
			if (property === "type" && internalContext._context.byProperty[value]) {
				value = internalContext._context.byProperty[value];
			}
			const propertyType = suppliedPropertyType || internalContext._context.propertyType[property];
			let patchText = '<' + bo._iri + '> <' + propertyIRI + '> ';
			if (propertyType === "@id") {
				patchText += '<' + value + ">";
			}
			else {
				patchText += '"' + escapeText(value) + "\"";
				// Need a check here because the language may be defined in the context
				if (lang && typeof lang !== "undefined" && lang !== "") {
					patchText += "@" + lang;
				}
				// Check if datatype defined in context or if a datatype supplied
				// If supplied that overrided the context datatype, but we would not expect that to happen
				// Really the datatype would only supplied on odd occassions where it is not possible to define in context
				// skos:notation is a good example of potential varying datatypes
				if (propertyType != null || datatype != null) {
					let updateType = propertyType;
					if (datatype) updateType = datatype;
					patchText += "^^<" + updateType + ">";
				}
			}
			// As in update property at this point we probably need some handling for adding JSON native types
			// Where we append the correct datatype to the string - otherwise everything is a string
			createAddPatch(patchText, bo._iri, graph, propertyGraphIRI);
		};

		// Move an item within an @list (zero indexed)
		this.move = function (iri, graph, property, sourceIndex, targetIndex) {
			if (!activeSubscription) throw new NoActiveSubscription();
			const id = graph + "___" + iri;
			const bo = boundObject[id];
			if (bo === undefined) {
				throw new InvalidUpdateOperationException("Invalid IRI supplied for move operation");
			}
			//if (loadedChannels[id].pendingDeletion) throw new InvalidUpdateOperationException("Attempting to update a deleted resource");
			const data = loadedChannels[id];
			const propertyIRI = internalContext._context.byProperty[property];
			if (propertyIRI == null) {
				throw new InvalidUpdateOperationException("Attempting to add an invalid property: " + property);
			}
			if (!isPropertyLoaded(iri, graph, propertyIRI)) {
				throw new InvalidUpdateOperationException("Cannot move a property that is not loaded: " + property);
			}
			if (typeof sourceIndex !== "number" || typeof targetIndex !== "number") {
				throw new InvalidUpdateOperationException("Indexes for move operations must be numbers");
			}
			if (parseInt(sourceIndex) < 0 || parseInt(targetIndex) < 0) {
				throw new InvalidUpdateOperationException("Indexes for move operations must be a value greater than zero");
			}
			const containerType = internalContext._context.propertyContainer[property];
			if (containerType !== '@list') {
				throw new InvalidUpdateOperationException("It is not possible to use moveItem on an object that is not a list");
			}
			// We need to compute the 'chunk' of the list we are moving. This from the lower to upper indexes
			const propertyType = internalContext._context.propertyType[property];
			let replaceItem;
			if (propertyType === "@id") {
				replaceItem = "<" + data[property][sourceIndex] + ">";
			}
			else {
				replaceItem = '"' + getPropertyValueText(iri, graph, property, targetIndex) + '"';
			}
			// We have to actually break this in to two operations. That does give a small chance of a failure in between of course
			const patchDeleteText = '<' + bo._iri + '> <' + propertyIRI + '> ' + sourceIndex + '..' + (parseInt(sourceIndex) + 1) + " ()";
			const patchInsertText = '<' + bo._iri + '> <' + propertyIRI + '> ' + targetIndex + '..' + targetIndex + ' (' + replaceItem + ")";
			createUpdateListPatch(patchDeleteText, bo._iri, graph);
			createUpdateListPatch(patchInsertText, bo._iri, graph);
		};

		// Remove an item within an @list (zero indexed)
		this.remove = function (iri, graph, property, index) {
			if (!activeSubscription) throw new NoActiveSubscription();
			const id = graph + "___" + iri;
			const bo = boundObject[id];
			if (bo === undefined) {
				throw new InvalidUpdateOperationException("Invalid IRI supplied for remove operation");
			}
			//if (loadedChannels[id].pendingDeletion) throw new InvalidUpdateOperationException("Attempting to update a deleted resource");
			const propertyIRI = internalContext._context.byProperty[property];
			if (propertyIRI == null) {
				throw new InvalidUpdateOperationException("Attempting to remove using an invalid property: " + property);
			}
			if (!isPropertyLoaded(iri, graph, propertyIRI)) {
				throw new InvalidUpdateOperationException("Cannot remove a property that is not loaded: " + property);
			}
			if (typeof index !== "number") {
				throw new InvalidUpdateOperationException("The index for a remove operations must be a number");
			}
			if (parseInt(index) < 0) {
				throw new InvalidUpdateOperationException("The index for a remove operation must be a value greater than zero");
			}
			const containerType = internalContext._context.propertyContainer[property];
			if (containerType !== '@list') {
				throw new InvalidUpdateOperationException("It is not possible to use remove an object that is not a list");
			}
			const patchText = '<' + bo._iri + '> <' + propertyIRI + '> ' + index + '..' + (parseInt(index) + 1) + " ()";
			createUpdateListPatch(patchText, bo._iri, graph);
		};

		// Assuming IRI for now
		// Later we will support an index which controls insert position
		// Default is to append
		this.insert = function (insertIri, graph, property, iri) {
			if (!activeSubscription) throw new NoActiveSubscription();
			const id = graph + "___" + insertIri;
			const bo = boundObject[id];
			if (bo === undefined) {
				throw new InvalidUpdateOperationException("Invalid IRI supplied for list insert operation");
			}
			//if (loadedChannels[id].pendingDeletion) throw new InvalidUpdateOperationException("Attempting to update a deleted resource");
			if (iri === null || iri === undefined) {
				throw new InvalidUpdateOperationException("An IRI must be supplied for a list insert operation");
			}
			const data = loadedChannels[id];
			const listLength = data[property] ? data[property].length : 0;
			const propertyIRI = internalContext._context.byProperty[property];
			if (propertyIRI == null) {
				throw new PropertyNotAvailableException("Attempting to insert into a list using an invalid property: " + property);
			}
			if (!isPropertyLoaded(insertIri, graph, propertyIRI)) {
				throw new InvalidUpdateOperationException("Cannot insert a property that is not loaded: " + property);
			}
			const containerType = internalContext._context.propertyContainer[property];
			if (containerType !== '@list') {
				throw new InvalidUpdateOperationException("It is not possible to use insert on a property not defined as a list");
			}
			// If trying to insert to a non-existent list we first create an empty list
			if (!data[property]) {
				const listCreateText = '<' + bo._iri + '> <' + propertyIRI + '> <http://www.w3.org/1999/02/22-rdf-syntax-ns#nil>';
				createAddPatch(listCreateText, bo._iri, graph);
			}
			const patchText = "<" + bo._iri + "> <" + propertyIRI + "> " + listLength + ".." + listLength + " (<" + iri + ">)";
			createUpdateListPatch(patchText, bo._iri, graph);
		};

		this.getProjectIRIs = function () {
			const uuid = uuidv4();
			const projectIRI = "https://grafsync.graphifi.com/data/project/" + uuid;
			const graphIRI = "https://grafsync.graphifi.com/graph/project/" + uuid;
			return {
				projectIRI,
				graphIRI
			};
		};

		this.isGraphLocked = function (graphIRI) {
			return lockedGraphs.includes(graphIRI);
		};

		this.getProjectLanguages = async function (projectType) {
			if (!activeSubscription) throw new NoActiveSubscription();
			const subscriptionId = activeSubscription.substring(SUBSCRIPTION_NAMESPACE.length);
			const response = await apiManager.sendData(`/subscription/` + subscriptionId + "/projectlanguages",
				{
					projectType,
					subscription: activeSubscription
				}, { accept: "application/json" });
			return response.json();
		};

		// We create a new resource of a given type
		// We allow for additional channels because resource creation obviously will not get picked up by a client
		// But the data may be needed by an application and it should inform others if this is case
		// status is boolean. true is immediately live and available. false indicates an additional property update should be following
		// Any pending resource will get cleaned up if no relationship exists after a certain amount of time
		this.insertResource = async function (jsonld, iri, graph, additionalResources, reservationID) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (graph === "" || graph === undefined) throw new Error("No graph scope has been set: ", iri);
			// TBH having to supply both is a bit redundant
			if (jsonld.id !== iri) throw new InvalidCreationException("id property value does not match supplied IRI.");
			const id = graph + "___" + iri;
			if (loadedChannels[id]) throw new InvalidCreationException("Resource already exists");
			if (additionalResources && !Array.isArray(additionalResources)) throw new InvalidCreationException("Additional resources must be an array.");
			if (!jsonld.type) throw new InvalidCreationException("A type must be included with a new resource.");
			if (jsonld.inNamedGraph) throw new InvalidCreationException("inNamedGraph is managed by Graphologi and should not be included in payload.");
			if (jsonld.dateCreated) throw new InvalidCreationException("dateCreated is managed by Graphologi and should not be included in payload.");
			await createInsertResourcePatch(jsonld, iri, graph, additionalResources, reservationID);
		};

		function createAddPatch(patchText, iri, graph, propertyGraphIRI) {
			createPatch('Add', patchText, iri, graph, propertyGraphIRI);
		}

		function createDeletePatch(patchText, iri, graph, propertyGraphIRI) {
			createPatch('Delete', patchText, iri, graph, propertyGraphIRI);
		}

		function createReplacePatch(patchText, iri, graph) {
			createPatch('Replace', patchText, iri, graph);
		}

		function createUpdatePatch(patchText, iri, graph, propertyGraphIRI) {
			createPatch('Update', patchText, iri, graph, propertyGraphIRI);
		}

		function createUpdateListPatch(patchText, iri, graph) {
			createPatch('UpdateList', patchText, iri, graph);
		}

		function createPatch(patchType, patchText, iri, graph, propertyGraphIRI) {
			if (graph === "" || graph === undefined) throw new Error("No graph scope has been set: ", iri);
			// Convert carriage returns to Turtle escaped text
			patchText = patchText.replace(/\n/g, "\\n");
			const change = {
				changeType: 'patch',
				text: patchType + ' ' + patchText + " .",
				session: sessionUUID,
				resourceIRI: iri,
				graphIRI: graph,
				...(propertyGraphIRI) && { propertyGraphIRI }
			};
			addRevision(change, iri);
		}

		this.isDeleted = function (iri, graph) {
			const deletedID = graph + "___" + iri;
			return deletedResources.includes(deletedID);
		};

		this.deleteResource = function (iri, graph) {
			if (!activeSubscription) throw new NoActiveSubscription();
			if (graph === "" || graph === undefined) throw new Error("No graph scope has been set: ", iri);
			deletedResources.push(graph + "___" + iri);
			const change = {
				changeType: 'delete',
				text: 'DeleteResource <' + iri + "> .",
				session: sessionUUID,
				resourceIRI: iri,
				graphIRI: graph
			};
			addRevision(change, iri);
		};

		// For now we expect n-triples to be supplied and a status of LIVE or PENDING
		async function createInsertResourcePatch(data, iri, graph, additionalResources, reservationID) {
			const context = framesManager.getFrame();
			if (!data.type) throw new InvalidCreationException("One or more types must be supplied");
			const typeList = Array.isArray(data.type) ? data.type : [data.type];
			const typeMapping = typeList.map(item => internalContext._context.byProperty[item] || item);
			// Think we can strip this after creating RDF
			let dataWithContext = cloneDeep(data);
			dataWithContext["@context"] = context["@context"];
			let quads = await jsonld.toRDF(dataWithContext, { format: 'application/n-quads' });
			// We add inNamedGraph to mimic the server generated property
			// We do it after conversion to RDF because the server manages the property so not needed
			data.inNamedGraph = graph;
			const change = {
				changeType: 'insert',
				...(reservationID) && { reservationID },
				text: 'InsertResource ' + quads,
				jsonld: data,
				session: sessionUUID,
				resourceIRI: iri,
				graphIRI: graph,
				classIRI: typeMapping
			};
			lastInsertedResource = iri;
			addRevision(change, iri, additionalResources);
		}

		// We will eventually need to handle the revision pending and confirmed
		// logic here but for now just send
		function addRevision(revision, iri, additionalResources) {
			let revisionItem = {
				object: revision,
				channel: revision.graphIRI + "___" + iri,
				additionalResources: additionalResources
			};

			// Of course it would make far more sense to 'build' parse objects
			// rather than a patch now that we are doing it in two stages
			// objects will for now only ever have one object associated with it
			let objects = patcher.getObjects(revision.text, revision.graphIRI, revision.propertyGraphIRI);
			// Expand the revision item properties to hold the parsed objects
			revisionItem.parsedObjects = objects;

			// ============================================
			// This will cause the update to happen locally
			// ============================================
			processResource(iri, revision.graphIRI, objects, revisionItem);
		}

		function doesImpactEntailment(objects) {
			// Compute if change will impact entailment
			// There is a pretty big cost to computing it for large numbers of properties so avoid if possible
			const predicate1 = objects[0].getPredicate ? objects[0].getPredicate().getText() : null;
			const predicate2 = objects[0].getPredicateReplacement ? objects[0].getPredicateReplacement().getText() : null;
			let hasPredicate = predicate1 || predicate2;
			let impactEntailmentProps = [];
			// We compute a list of predicates that might be impacted by the entailment
			// That helps us minimise recomputation
			if (hasPredicate) {
				impactEntailmentProps = chainEntailments.filter(em => {
					return em.propertyChain.includes(predicate1) || em.propertyChain.includes(predicate2);
				})
					.map(em => em.subPropertyOf);
			}
			return impactEntailmentProps.length > 0 ? impactEntailmentProps : false;
		}

		function getObjectLanguage(obj) {
			const lang = obj.constructor === LDreplace || obj.constructor === LDupdate ?
				obj.getObjectReplacement().getLanguage ?
					obj.getObjectReplacement().getLanguage()
					:
					null
				:
				obj.getObject && obj.getObject().getLanguage ?
					obj.getObject().getLanguage()
					:
					null;
			return lang;
		}

		function processResource(iri, graphIRI, objects, revisionItem) {
			const id = graphIRI + "___" + iri;
			const impactEntailment = doesImpactEntailment(objects);
			const lang = getObjectLanguage(objects[0]);
			// We need to remove entailment at this point because replace operations are split into a delete and add
			// And things go belly up if we don't
			// We only remove the impacted properties			
			if (impactEntailment) removeEntailments([{
				graph: graphIRI,
				iri
			}], lang, impactEntailment.map(ie => internalContext._context.byIRI[ie]));
			// Will not be 'loaded' for inserts
			if (loadedChannels[id]) {
				loadedChannels[id]._version = loadedChannels[id]._version ? loadedChannels[id]._version + 1 : 1;
			}
			// This causes the local update
			processor.parse(objects, revisionItem.object);
			if (impactEntailment) computeEntailment([{
				graph: graphIRI,
				iri
			}], lang);
			pendingRevisions.push(revisionItem);
			// Initialise bridge array for this channel if necessary
			// For a new insert this will always be the case
			if (bridge[id] == null) {
				bridge[id] = [];
			}
			// Again, assuming only 1 item for now
			// Add new item to the end of the bridge (we can do this as is because we know the bridge
			// is in the right state because we constantly update it after server operations are received)
			//console.log("Adding edit to bridge: ", objects[0].buildPatch())
			bridge[id].push(objects[0]);
			processPendingRevisions();
		}

		// Pending revisions are client revisions not yet sent to the server
		// We only ever send one revision at a time and wait for acknowledgement that it is processed
		function processPendingRevisions() {
			/*	const current = new Date();
				const year = current.getFullYear();
				const month = current.getMonth();
				const day = current.getDate();
				const hours = current.getHours();
				const minutes = current.getMinutes() + 1;
				const timeoutPoint = new Date(year, month, day, hours, minutes, 0, 0);
				logMessage("Timeout point: " + timeoutPoint.toLocaleString());
				const timeoutDiff = timeoutPoint.valueOf() - new Date().valueOf();
				setTimeout(() => {*/
			// No pending revisions so nothing to process
			if (pendingRevisions.length === 0) return;
			// If we are waiting for the server to acknowledge a previously sent revision do nothing
			if (waitingForAck) return;
			// For testing we might want to pause sending messages
			if (pauseSending) return;
			let pr = pendingRevisions[0];
			const id = pr.channel;
			// We need to insert the latest revision number we have for the channel to inform the server
			// For a new resource this will always be true
			if (lastSyncedRevisionNo[id] == null) {
				lastSyncedRevisionNo[id] = 0;
			}
			pr.object.revisionNo = lastSyncedRevisionNo[id];
			//console.log("Updating pending revision with transformed, rooted operation");
			//console.log("Initial operation from pending revision[0] is ...");
			//logOperation(pr.parsedObjects[0]);
			let bridgeOp = bridge[id][0];
			//console.log("Rooted operation from bridge[0] ... channel " + pr.channel);
			// The rooted operation is the one that OT happens against - it is the common point shared with the server
			//logOperation(bridgeOp);
			if (bridgeOp.constructor === LDnoop) {
				// No point sending a No-op, which although not generated
				// locally may have been generated in bridge building
				//console.log("Not sending No-op ...");
				// Now remove the pending revision as has been processed
				pendingRevisions.shift();
				// And also remove from bridge
				bridge[id].shift();
			} else {
				// The first item on the bridge holds the transformed local
				// operation that will be rooted in server space. We update
				// the pending revision with that
				pr.parsedObjects[0] = bridgeOp;
				// Updating patch instruction in pending revision to match bridge also
				//console.log("Updated patch from bridge is: " + bridgeOp.buildPatch());
				pr.object.text = bridgeOp.buildPatch();
				// Storing as sent before emitting might be subject to failure problems
				// so probably need to refactor here a bit
				// additionalResources is an array of other resources that need to know about this revision
				let message = pr.object;
				// We don't need to send any JSON-LD for inserts so remove
				// Only needed for local processing
				if (message.jsonld) delete (message.jsonld);
				if (socket) {
					//logMessage("Sending message");
					//logMessage(JSON.stringify(message, null, 1));
					socket.emit(socketEvent, {
						server: apiManager.getServer(),
						channel: pr.channel,
						subscription: activeSubscription,
						message,
						additionalResources: pr.additionalResources
					});
					// Now remove the pending revision as has been processed
					pendingRevisions.shift();
					waitingForAck = true;
				}
				else {
					logMessage("Cannot send message as no socket");
				}
			}
			//				}, timeoutDiff);
		}

		function processIncomingRevision(message, channel, isAdditionalChannel = false, processAck = true) {
			// We may start to receive messages between asking to load a resource and
			// starting to process so we store ready to sync after load
			const id = channel;
			if (message.changeType !== "insert" && loadedChannels[id] == null) {
				if (storedMessages[id] == null) {
					storedMessages[id] = [];
				}
				storedMessages[id].push(message);
			} else {
				// We only process changes from other user sessions, ignoring this session.
				if (message.session !== sessionUUID) {
					// We only process messages at a revision later than that which we have loaded
					// If the DB is ahead of the server we pause until server catches up
					// Only exception is inserts in the edge case where we have created a duplicate IRI locally
					// Locally will be at revision 0 and so will incoming revision, which will take precedence in OT
					// Last synced will be undefined if we get an insert resource message which we don't have loaded
					if (lastSyncedRevisionNo[id] === undefined || message.revisionNo > lastSyncedRevisionNo[id]
						|| (message.revisionNo === 0 && lastSyncedRevisionNo[id] === 0)) {
						// We need to transform server change against client changes ...
						let objects = patcher.getObjects(message.text, message.graphIRI, message.propertyGraphIRI);
						let transformedObjects = performOT(objects, channel);
						const impactEntailment = doesImpactEntailment(transformedObjects);
						const lang = getObjectLanguage(transformedObjects[0]);
						if (loadedChannels[id]) {
							// Probably need to test if lang exists
							if (impactEntailment) removeEntailments([{
								graph: message.graphIRI,
								iri: message.resourceIRI
							}], lang, impactEntailment.map(ie => internalContext._context.byIRI[ie]));
							loadedChannels[id]._version = loadedChannels[id]._version ? loadedChannels[id]._version + 1 : 1;
						}
						// Now apply transformed object to local data
						//console.log("Transformed incoming object");
						//console.log(transformedObjects[0].buildPatch())
						processor.parse(transformedObjects, message);
						// In the situation where a replaced resource is replaced with nothing that indicates an insertion clash
						// Local processing will have cleaned up but we also unload to remove any channel listening.
						if (transformedObjects[0].constructor === LDreplaceResource && transformedObjects[0].getEmptyReplacement()) {
							unloadResources(transformedObjects[0].getIRI(), transformedObjects[0].getGraph().getText());
						}
						lastSyncedRevisionNo[id] = message.revisionNo;
						if (impactEntailment) computeEntailment([{
							graph: message.graphIRI,
							iri: message.resourceIRI
						}], lang);
					}
					else {
						console.log("Ignoring message at revision " + message.revisionNo + " for channel " + id);// eslint-disable-line
					}
				} else {
					lastSyncedRevisionNo[id] = message.revisionNo;
					//console.log("Now synced at revision no: " + message.revisionNo);
					//console.log("Ignoring change by self ...");
					// If this is an additional channel we don't want the bridge affected as we will have already updated
					if (!isAdditionalChannel) {
						// We've received acknowledgement of our message so we can remove the message from the bridge
						// The incoming transformed message should be the same as our bridge entry!!!
						//console.log("Bridge length for channel: " + channel + " is " + bridge[channel].length);
						//console.log("Removing first item from bridge ... channel "+ channel);
						//logOperation(bridge[channel][0]);
						bridge[id].shift();
						//console.log("Next bridge item is ...");
						// processAck will be false if we're executing catchup 
						// We only want ack altered if actually processing client edits
						if (processAck) {
							waitingForAck = false;
							lastAcknowledgedID = id;
						}
					}
					// We've received acknowledgement of this sessions's change from the
					// server so can now process the next change
					processPendingRevisions();
				}
			}
		}

		function performOT(serverObjects, id) {
			// Transform server object against bridge
			let transformedObjects = [];
			let newBridge = [];
			// By default we start with the transformed object being the first object
			let transformedServerObject = serverObjects[0];
			if (bridge[id] != null) {
				for (let i = 0; i < bridge[id].length; i++) {
					let clientObject = bridge[id][i];
					let bridgeClientObject;
					// If the transformed server object is a No-op then the transform
					// will simply be the client object
					if (transformedServerObject.constructor === LDnoop) {
						bridgeClientObject = clientObject;
					} else {
						if (clientObject.constructor === LDnoop) {
							// If the local operation is a No-op it stays that way on
							// the bridge because it has no effect even when transformed
							bridgeClientObject = clientObject;
						} else {
							bridgeClientObject = btp.applyTransform(clientObject, transformedServerObject);
						}
					}
					//console.log("Transformed bridge object ...");
					//logOperation(bridgeClientObject);
					newBridge.push(bridgeClientObject);
					// Get the local operation
					//console.log("Processing local operations ...");
					// If the client object is a No-op then no point in transforming as server object will not be affected
					if (clientObject.constructor !== LDnoop) {
						// Similarly if server object is a No-op the transform will again be the server object
						if (transformedServerObject.constructor !== LDnoop) {
							const transformResult = tp.applyTransform(clientObject, transformedServerObject);
							transformedServerObject = transformResult.action;
							if (transformResult.messages && transformResult.messages.length > 0) {
								notify(EVENT_CONCURRENT_EDIT, { text: transformResult.messages });
							}
						}
					}
				}
				// Now replace bridge with newly calculated bridge
				bridge[id] = newBridge;
			}
			let messages = [];
			if (transformedServerObject.constructor === LDdeleteResource) {
				messages.push("Another user has just deleted " + transformedServerObject.getIRI().getText());
				notify(EVENT_EDIT, { text: messages });
			}
			else if (transformedServerObject.constructor === LDinsertResource) {
				notify(EVENT_RESOURCE_CREATE, {
					text: messages,
					channel: transformedServerObject.getIRI().getText(),
					class: transformedServerObject.getInsertClasses()[0]
				});
			}
			// Now store the resulting transformed server operation
			//console.log("Transformed object ...");
			//logOperation(transformedServerObject);
			transformedObjects.push(transformedServerObject);
			return transformedObjects;
		}

		//function logOperation(object) {
		//	console.log("Object patch " + object.buildPatch());
		//}

		this.isMaterialising = function () {
			return isMaterialising;
		};

		this.materialise = function (shoudMaterialise) {
			isMaterialising = shoudMaterialise;
		};

		// ================ Change binding =======================

		// Expecting the IRI (id) of the resource to bind events for to be supplied
		this.bindChange = function (iri, graphIRI, callback) {
			if (!iri) throw new Error("An IRI must be supplied to bind events");
			const id = graphIRI + "___" + iri;
			const bo = boundObject[id];
			if (!bo) throw new Error("Attempting to bind an event to an unloaded resource: " + iri);
			if (!callback) throw new Error("A callback must be supplied when binding events");
			if (!bo._changeEvents) {
				bo._changeEvents = new Map();
			}
			const changeGuid = guid();
			bo._changeEvents.set(changeGuid, callback);
			return changeGuid;
		};

		this.unbind = function (iri, graphIRI, changeGuid) {
			// We check if initialised because when logging out or killing application this may get called
			// After GS has reset itself. We therefore do not need to unbind which will cause errors potentially if we try
			if (!isInitialised) return;
			if (!iri || iri === undefined)
				throw new Error("An IRI must be supplied for event unbinding");
			const id = graphIRI + "___" + iri;
			const bo = boundObject[id];
			// If GS.unload has been called there will be no event callback information available so
			// handle any remaining async calls to unbind by simply ignoring
			if (!bo) return;
			if (!bo._changeEvents) return;
			if (!changeGuid || changeGuid === undefined)
				throw new Error("A change GUID must be supplied for event unbinding");
			if (bo._changeEvents.has(changeGuid)) {
				bo._changeEvents.delete(changeGuid);
			}
		};

		// =================== CONTEXT PROCESSING ================

		function normaliseContext(context) {

			let newContext = {};
			const currentContext = context["@context"];

			// Again, assuming @id and @type here!!!!
			for (let prop in currentContext) {
				let id = null;
				let type = null;
				let container = null;
				let language = null;
				if (typeof currentContext[prop] === 'object') {
					id = currentContext[prop]["@id"];
					if (currentContext[prop]["@type"]) {
						type = currentContext[prop]["@type"];
					}
					if (currentContext[prop]["@container"]) {
						container = currentContext[prop]["@container"];
					}
					if (currentContext[prop]["@language"]) {
						language = currentContext[prop]["@language"];
					}
				} else {
					// In defined in this form there will only be an id
					id = currentContext[prop];
				}
				newContext[prop] = {};
				newContext[prop]["@id"] = id;
				newContext[prop]["@type"] = type;
				newContext[prop]["@container"] = container;
				newContext[prop]["@language"] = language;
			}

			return newContext;
		}

		function computeInternalContext(originalContext) {

			const currentContext = normaliseContext(originalContext);
			let ic = {};

			Object.defineProperty(ic, "_context", {
				value: {
					byProperty: {},
					byIRI: {},
					propertyType: {},
					propertyContainer: {},
					propertyLanguage: {}
				},
				writable: true
			});

			// We include the JSON-LD default values for @ properties
			ic._context.byIRI['@value'] = '@value';
			ic._context.byIRI['@graph'] = '@graph';
			ic._context.byIRI['@language'] = '@language';
			ic._context.byIRI['@id'] = '@id';
			ic._context.byIRI['@type'] = '@type';

			for (let prop in currentContext) {
				const propIRI = currentContext[prop]["@id"];
				const propType = currentContext[prop]["@type"];
				const propContainer = currentContext[prop]["@container"];
				const propLanguage = currentContext[prop]["@language"];
				if (propIRI) {
					ic._context.byProperty[prop] = propIRI;
					ic._context.byIRI[propIRI] = prop;
					if (propType && propContainer && propType !== "@id" && propContainer === "@list") {
						throw new Error("List containers not currently supported for literals.");
					}
					if (propType) ic._context.propertyType[prop] = propType;
					if (propContainer) ic._context.propertyContainer[prop] = propContainer;
					if (propLanguage) ic._context.propertyLanguage[prop] = propLanguage;
				}
			}
			ic._context.byIRI['http://www.w3.org/1999/02/22-rdf-syntax-ns#type'] = 'type';
			ic._context.propertyType['type'] = '@id';
			ic._context.byProperty['type'] = 'http://www.w3.org/1999/02/22-rdf-syntax-ns#type';
			return ic;
		}

		// ==================== EVENT HANDLING ====================

		let registrations = new Map();

		/**
		 * A notification to give to the client application
		 * @param {*} eventName 
		 * @param {*} eventObject Expecting form {text: Array, channel: IRI, class: IRI of resource class}
		 */
		function notify(eventName, eventObject) {
			//eventStream.push({type: eventName, data: eventObject});
			registrations.forEach(callback => callback({ type: eventName, data: eventObject }));
		}

		// Allow registrations for events within the application
		this.register = function (callback) {
			const regGuid = guid();
			registrations.set(regGuid, callback);
			return regGuid;
		};

		// Allow unregistrations for events within the application
		this.unregister = function (regGuid) {
			registrations.delete(regGuid);
		};

		// ============== DATA UNLOADING ==============
		// We do not want to be subscribed to rooms unnecessarily so we provide a means to unsubscribe

		// If there are pending revisions they might get lost if not processed
		this.outstandingEdits = function () {
			return pendingRevisions.length;
		};

		function checkEdits(resolve, reject) {
			if (checkingEdits) clearTimeout(checkingEdits);
			checkingEditsAttempts++;
			if (checkingEditsAttempts >= 20) {
				reject("Cannot complete operation.");
				checkingEditsAttempts = 0;
				return;
			}
			if (pendingRevisions.length === 0) {
				resolve();
				checkingEditsAttempts = 0;
				return;
			}
			checkingEdits = setTimeout(checkEdits(resolve, reject), 250);
		}

		this.waitForEditsToClear = function () {
			return new Promise((resolve, reject) => {
				if (checkingEditsAttempts > 0) reject("Already processing an operation.");
				checkingEditsAttempts = 0;
				checkEdits(resolve, reject);
			});
		};

		this.loadKey = function () {
			return loadKey;
		};

		this.unload = async function (iris = [], graphIRI) {
			return unloadResources(iris, graphIRI);
		};

		async function unloadResources(iris = [], graphIRI) {
			if (!Array.isArray(iris)) throw new Error("Unloading requires an array of IRIs to be supplied.");
			if (iris.length > 0 && (!graphIRI || graphIRI === undefined)) throw new Error("Attempting to unload specific IRIs with no graph provided.");
			if (!activeSubscription) return;
			if (socket && socket.connected) {
				// We unsubcribe and wait for that to happen (i.e. server confirmation)
				// to ensure no further messages arrive after unloading channels
				await new Promise((resolve, reject) => {
					try {
						socket.emit(socketEvent, {
							server: apiManager.getServer(),
							subscription: activeSubscription,
							unsubscribeFromChannel: iris.map(iri => graphIRI + "___" + iri)
						}, () => {
							resolve();
						});
					}
					catch (e) {
						console.log(e);// eslint-disable-line
						reject();
					}
				});
			}
			// Here we unload from loaded channels and boundObject array and any loaded models too
			// We also clear out unavailable resources as they may not be available (From an import for exaxmple)
			// We also clear out any stored messages, pending revisions and the bridge just in case
			// If no IRIs are given we unload everything
			if (iris.length === 0) {
				loadedChannels = {};
				boundObject = {};
				loadedModels = {};
				storedMessages = {};
				unavailableResources = new Set();
				pendingRevisions = [];
				deletedResources = [];
				bridge = {};
			}
			else {
				iris.forEach(iri => {
					const id = graphIRI + "___" + iri;
					delete loadedChannels[id];
					delete boundObject[id];
					delete storedMessages[id];
					delete bridge[id];
					pendingRevisions = pendingRevisions.filter(pr => pr.channel !== id);
				});
			}
		}

		// Are b predicates a subset of a's? We define equal as a subset
		function isPredicateSubset(a, b) {
			if (!b.predicates) return false;
			if (!a.predicates) return true;
			return b.predicates.every(iri => {
				const aHasIRI = a.predicates.includes(iri);
				return aHasIRI;
			});
		}

		// ============== MODEL GETTING ===============

		this.getSuperClasses = function (classIRI, graphIRI) {
			const hierarchy = getSuperClass(classIRI, graphIRI);
			return hierarchy;
		};

		this.getSubclasses = function (classIRI, graphIRI) {
			if (!loadedModels[graphIRI]) throw new Error("A model must be loaded before querying it.");
			const hierarchy = getSubclass(classIRI, graphIRI);
			return hierarchy;
		};

		this.getSuperProperties = function (propertyIRI, graphIRI) {
			const hierarchy = getSuperProperty(propertyIRI, graphIRI);
			return [].concat(hierarchy);
		};

		// This is useful for domain and range inheritance
		this.getSuperPropertyValues = function (propertyIRI, graphIRI, property) {
			const sp = this.getSuperProperties(propertyIRI, graphIRI);
			let spData = [];
			sp && sp.forEach(spItem => {
				const id = graphIRI + "___" + spItem;
				const spItemData = loadedChannels[id];
				if (spItemData[property]) {
					const spToAdd = Array.isArray(spItemData[property]) ? spItemData[property] : [spItemData[property]];
					spData = [...new Set([...spData, ...spToAdd])];
				}
			});
			return spData;
		};

		function getSuperClass(classIRI, graphIRI, list = []) {
			const id = graphIRI + "___" + classIRI;
			const data = loadedChannels[id];
			// If a class has been deleted won't be in model so can't get superclass
			if (!data) return [];
			if (data.subClassOf) {
				const subclassList = Array.isArray(data.subClassOf) ? data.subClassOf : [data.subClassOf];
				return [].concat.apply([data.id], subclassList.filter(li => !list.includes(li)).map(superClassIRI => getSuperClass(superClassIRI, graphIRI, list.concat(subclassList))));
			}
			else return data.id;
		}

		function getSuperProperty(propertyIRI, graphIRI, list = []) {
			const id = graphIRI + "___" + propertyIRI;
			const data = loadedChannels[id];
			// If a property has been deleted won't be in model so can't get superclass
			if (!data) return [];
			if (data.subPropertyOf) {
				const subpropertyList = Array.isArray(data.subPropertyOf) ? data.subPropertyOf : [data.subPropertyOf];
				return [].concat.apply([data.id], subpropertyList.filter(li => !list.includes(li)).map(superPropertyIRI => getSuperProperty(superPropertyIRI, graphIRI, list.concat(subpropertyList))));
			}
			else return data.id;
		}

		function getSubclass(classIRI, graphIRI, list = []) {
			const id = graphIRI + "___" + classIRI;
			const data = loadedChannels[id];
			// Certain concepts may be used but not loaded such as skos:Concept. That's fine - we just don't know the subclasses
			if (!data) return [];
			const subclassList = loadedModels[graphIRI]
				.filter(modelIRI => {
					const id = graphIRI + "___" + modelIRI;
					const data = loadedChannels[id];
					if (!data) {
						throw new Error("Unable to retrieve subclass information. The resource is: " + modelIRI + " " + graphIRI);
					}
					return data.subClassOf && data.subClassOf.includes(classIRI);
				});
			return [].concat.apply(subclassList, subclassList.filter(li => !list.includes(li)).map(subclassResource => getSubclass(subclassResource, graphIRI, list.concat(subclassList))));
		}

		/**
		 * We need to load models for super and subclass tests
		 */
		this.loadModel = async function (graphs) {
			let iriSet = Array.isArray(graphs) ? graphs : [graphs];
			iriSet = iriSet.filter(iri => !loadedModels[iri]);
			if (iriSet.length === 0) return;
			iriSet.forEach(iri => loadedModels[iri] = []);
			try {
				const payload = {
					socketId: socket.id,
					graph: Array.isArray(iriSet) ? iriSet : [iriSet],
					includeAuth: true,
					subscription: activeSubscription
				};
				let response = await apiManager.sendData("/model", payload, { accept: "application/ld+json" });
				// Just in case somebody logs out whilst we're witing for responses
				if (!isInitialised) return;
				const data = await response.json();
				let resourceSet = null;
				// Map the graphs for each resource
				if (data.graph) {
					resourceSet = data.graph;
					resourceSet.forEach(resource => {
						const namedGraph = resource.inNamedGraph;
						if (!loadedModels[namedGraph].includes(resource.id)) loadedModels[namedGraph].push(resource.id);
					});
				}
				else {
					const keys = Object.keys(data);
					// If keys is 1 then it's only a context so no data could be retrieved
					if (keys.length > 1) resourceSet = [data];
				}
				processLoadedData(data.graph, data.catchUp);
			}
			catch (e) {
				console.log(e);// eslint-disable-line
				throw new Error(e.message);
			}
		};

		// =============== DATA GETTING ===============
		// Here we 'get' data, which means a client doesn't need to care if loaded or not.
		// If not loaded GS will load those IRIs as needed and then return a merge with any already loaded
		// That means this function needs to be fast because it will get called a lot potentially

		// If just a list or IRIs are supplied we use the current scoped IRI to compute graphs for those IRIs
		this.loadData = async function (iriSet, options = {}, graph, processEntailments = true, entailmentOptions = [], excludeDeprecated = false) {
			if (typeof processEntailments !== "boolean") {
				console.log("Incorrect entailment property value: " + processEntailments);// eslint-disable-line
				return;
			}
			if (typeof excludeDeprecated !== "boolean") {
				console.log("Incorrect deprecation exclusion value: " + excludeDeprecated);// eslint-disable-line
				return;
			}
			if (!activeSubscription) throw new NoActiveSubscription();
			if (!iriSet || iriSet.length === 0) return;
			if (graph === "" || graph === undefined || graph === true || graph === false) throw new Error("No graph scope has been set: ", iriSet);
			let resourceData = iriSet.map(iri => {
				return {
					graph,
					iri
				};
			});
			return this.loadDataFromGraph(resourceData, options, processEntailments, entailmentOptions, excludeDeprecated);
		};

		this.isLoadingInFlight = function () {
			return inFlight.size;
		};

		// Given a language or array of languages do we already have those languages loaded?
		function needToLoadLangs(langs, id) {
			const langOptions = boundObject[id]._loadOptions.language;
			if (!langs && !langOptions) return false;
			if (langs && !langOptions) return false;
			const languages = Array.isArray(langs) ? langs : [langs];
			// Need to check if languages is a subset of lang options;
			const isNotLoaded = languages.some(lang => !langOptions.includes(lang));
			return isNotLoaded;
		}

		// We allow for one predicate to be counted (which is a common use case for tree like children displays)
		// entailmentOptions are the loading options for entailed resources
		this.loadDataFromGraph = async function (resourceData, options = {}, processEntailments = true, entailmentOptions = [], excludeDeprecated = false) {
			if (typeof processEntailments !== "boolean") {
				console.log("Incorrect entailment property value: " + processEntailments);// eslint-disable-line
				return;
			}
			try {
				if (!activeSubscription) throw new NoActiveSubscription();
				if (typeof resourceData === "undefined") throw new InvalidParameter("Data loading has been supplied with an undefined value");
				// Filter out anything already loaded as best we can
				// It's always possible multiple requests for the same resource could happen at the same time
				const unloadedResourceData = resourceData.filter(resource => {
					// Don't try and reload an unavailable resource
					const id = resource.graph + "___" + resource.iri;
					if (unavailableResources.has(id)) return false;
					if (!loadedChannels.hasOwnProperty(id)) return true;
					if (boundObject[id]._loadOptions) {
						// In this instance we have already done a full load so do not then to a partial load
						if (isEmpty(boundObject[id]._loadOptions)) return false;
						// Loading a new language so need to load
						if (needToLoadLangs(options.language, id)) return true;
						// In this instance we check if a partial load is same as existing partial load
						const areLoadOptionsAsubset = isPredicateSubset(boundObject[id]._loadOptions, options);
						if (areLoadOptionsAsubset) {
							// Predicates are a subset and no tests so no need to load
							if (!options.tests && !boundObject[id]._loadOptions.tests) return false;
							if (options.tests && boundObject[id]._loadOptions.tests) {
								// If predicates are a subset and tests are the same then no need to load
								if (JSON.stringify(options.tests) === JSON.stringify(boundObject[id]._loadOptions.tests)) return false;
							}
						}
						// Otherwise are both options not exactly the same in JSON string terms. If not we load
						if (JSON.stringify(options) !== JSON.stringify(boundObject[id]._loadOptions)) return true;
					}
					return false;
				});
				if (unloadedResourceData.length === 0) return;
				// We subscribe to socket IO rooms first to ensure we don't miss any updates
				// There will be a corresponding permissions check on the other end of the socket
				const { predicates, language, tests, countPredicates, includeAuth, resourceFilters } = options;
				// We only retrieve the subset of unloaded that are also not inflight already
				const notInFlightSet = unloadedResourceData.filter(resource => {
					const id = resource.graph + "___" + resource.iri;
					const isInFlight = inFlight.get(id);
					if (!isInFlight) return true;
					// It's inflight for a full load so we can ignore this
					if (isInFlight && isEmpty(isInFlight.options)) return false;
					// It's inflight with the same options so we can ignore this
					if (JSON.stringify(isInFlight.options) === JSON.stringify(options)) return false;
					return true;
				});
				// We now get subset of not in flight that we are not subscribed to and for which we do not need to load a new language
				const notSubscribed = notInFlightSet.filter(resource => {
					const id = resource.graph + "___" + resource.iri;
					if (!loadedChannels.hasOwnProperty(id)) return true;
					if (!isEmpty(boundObject[id]._loadOptions)) {
						if (needToLoadLangs(options.language, id)) return true;
					}
					return false;
				});
				// Loading data from the API automatically subscribes to the channel if permissions are okay
				// Note we only load those resources that are not in flight in other requests
				const apiPromise = retrieveDataFromAPI(notInFlightSet, predicates, language, tests, countPredicates, includeAuth, resourceFilters, excludeDeprecated);
				// We create an array of promises - one for each unloaded item - which might contain duplicates
				// But if some IRIs are already inflight then they will be a different promise
				const promiseList = unloadedResourceData.map(resource => {
					const id = resource.graph + "___" + resource.iri;
					const isInFlight = inFlight.get(id);
					if (isInFlight && (isEmpty(isInFlight.options) ||
						(JSON.stringify(isInFlight.options) === JSON.stringify(options)))) return isInFlight.promise;
					inFlight.set(id,
						{
							promise: apiPromise,
							options: options
						});
					return apiPromise;
				});
				// Essentially here we wait for all the in flight requests to return and between 
				// the they will have all the necessary resources
				const response = await Promise.all(promiseList);
				// Just in case somebody logs out whilst we're witing for responses
				if (!isInitialised) return;
				let responsesToProcess = [];
				// Filter responses so we only process unique responses
				// NOT SURE THIS IS DOING ANY FILTERING AT THE MOMENT!!!!
				response.forEach(resp => {
					if (responsesToProcess.includes(resp)) return;
					responsesToProcess.push(resp);
				});
				let returnedIds = [];
				responsesToProcess.forEach(dataSet => {
					const resourceSet = dataSet.graph;
					resourceSet.forEach(resource => {
						if (resource.id) returnedIds.push(resource.inNamedGraph + "___" + resource.id);
					});
				});
				// We keep a global log of unavailable resources to avoid attempting to reload in future
				if (!excludeDeprecated) {
					notSubscribed
						.filter(resource => !returnedIds.includes(resource.graph + "___" + resource.iri))
						.forEach(resource => {
							const id = resource.graph + "___" + resource.iri;
							unavailableResources.add(id);
						});
				}
				responsesToProcess.forEach(dataSet => processLoadedData(dataSet.graph, dataSet.catchUp, options));
				if (processEntailments) {
					// Definitely want to optimise this to only compute entailment where change might impact the results
					await loadEntailedResources(resourceData, entailmentOptions);
					// Just in case somebody logs out whilst we're waiting for responses
					if (!isInitialised) return;
					if (notInFlightSet.length > 0) computeEntailment(notInFlightSet);
				}
				unloadedResourceData.forEach(resource => {
					const id = resource.graph + "___" + resource.iri;
					inFlight.delete(id);
				});
				//console.log(Object.keys(loadedChannels).length);
			}
			catch (e) {
				throw new Error(e);
			}
		};

		// So here we move forward through chains to ensure we have the necessary loaded
		async function loadEntailedResources(resourceData, options = []) {
			// Assuming only two level property chains at the moment
			let level1 = chainEntailments.map(em => em.propertyChain[0]);
			let ars = [];
			resourceData.forEach(resource => {
				const id = resource.graph + "___" + resource.iri;
				const data = loadedChannels[id];
				if (data) {
					Object.keys(data).forEach(property => {
						const propertyIRI = internalContext._context.byProperty[property];
						if (level1 && level1.includes(propertyIRI)) {
							if (Array.isArray(data[property])) {
								data[property].forEach(iri => {
									// Dedupe as different properties may entail same value
									if (!ars.find(ar => ar.graph === resource.graph && ar.iri === iri)) {
										ars.push({
											graph: resource.graph,
											iri
										});
									}
								});
							}
							else {
								// Dedupe as different properties may entail same value
								if (!ars.find(ar => ar.graph === resource.graph && ar.iri === data[property])) {
									ars.push({
										graph: resource.graph,
										iri: data[property]
									});
								}
							}
						}
					});
				}
			});
			// We set entailment to false for now because our uses cases only need simple stuff
			// Again assuming two level chains
			const level2Set = new Set(chainEntailments.map(em => em.propertyChain[1]));
			// Need to update this at some point so configuration defines that inScheme should be used
			// if literalForm property is the entailment property
			// Other loads used both properties so we need both to reduce API hits
			const level2 = [...level2Set, "http://www.w3.org/2004/02/skos/core#inScheme"];
			if (ars.length > 0) {
				await instance.loadDataFromGraph(ars,
					{
						predicates: level2,
						...(options.length > 0) && ({ resourceFilters: options })
					},
					false);
			}
		}

		// Can probably optimise this to reduce when computation is needed
		// For instance no point computing if the domain of the resource being checked does not match the domain for entailment props
		function computeEntailment(resourceData, lang) {
			isMaterialising = true;
			// First we chain backwards to find out the list of resources that are impacted by this set of resource updates
			// And compute which chains come in to play during that processing
			let entailedResources = [];
			let notUsedByBackwardsChains = [];
			const backwardsAffectingChains = chainEntailments.filter(em => {
				const erItems = entailedResources.length;
				processChainItemBackwards(resourceData, [...em.propertyChain], em.subPropertyOf, entailedResources);
				// Keep a track of any chains not used by backwards chaining as candidates needed by forward chaining
				if (entailedResources.length <= erItems) notUsedByBackwardsChains.push(em);
				return entailedResources.length > erItems;
			});
			// Now we add the backward computed list to the supplied list making sure we one include a resource once if in both lists
			let uniqueResourceSet = cloneDeep(resourceData);
			entailedResources.forEach(resource => {
				const existsInUnique = uniqueResourceSet.some(uniqueResource => uniqueResource.graph === resource.graph && uniqueResource.iri === resource.iri);
				if (!existsInUnique) uniqueResourceSet.push(resource);
			});
			// Now process that list to check if any resource is at the start of a chain
			const forwardsAffectingChains = notUsedByBackwardsChains.filter(em => {
				const property = internalContext._context.byIRI[em.propertyChain[0]];
				return resourceData.some(resource => {
					const id = resource.graph + "___" + resource.iri;
					const data = loadedChannels[id];
					// Might not be loaded
					if (!data) return false;
					return data[property];
				});
			});
			const affectingChains = [...backwardsAffectingChains, ...forwardsAffectingChains];
			// Get the alias properties that are impacted by affectings entailment chains
			const affectingProperties = affectingChains.map(em => internalContext._context.byIRI[em.subPropertyOf]);
			// First we remove entailments where necessary - that is, only on properties that are affected by update
			// Elsewhere we have already removed them in certain situations but this will remove them from
			// any backward chained resources as well
			removeEntailments(uniqueResourceSet, lang, affectingProperties);
			// We now process only chains that can have an effect to add in entailments
			let chainActions = [];
			affectingChains.forEach(em => processChainItem(chainActions, uniqueResourceSet, [...em.propertyChain], lang, em.subPropertyOf));
			// We process here to avoid unwanted renders
			// Doing it this way we can work out if the last entailment and set GS so components can detect and update
			for (let action = 0; action < chainActions.length; action++) {
				if (action === chainActions.length - 1) {
					isMaterialising = false;
				}
				let chainAction = chainActions[action];
				loadedChannels[chainAction.subjectId]._version =
					loadedChannels[chainAction.subjectId]._version ?
						loadedChannels[chainAction.subjectId]._version + 1
						:
						1;
				processor.parse([chainAction.action], { session: sessionUUID }, true);
			}
			// Set is here too in case of no actions
			isMaterialising = false;
		}

		function removeEntailments(resourceData, lang, affectingProperties) {
			if (!resourceData || resourceData.length === 0) return;
			resourceData.forEach(resource => {
				const id = resource.graph + "___" + resource.iri;
				const data = loadedChannels[id];
				if (data && data._entailed) {
					Object.keys(data._entailed).forEach(prop => {
						// We either process all entailments or only properties we know are affected
						// First we remove the entailed values
						if (!affectingProperties || affectingProperties.includes(prop)) {
							const propValue = data._entailed[prop];
							// Entailed language info e.g. "prefLabel": {"en": ["b", "c"]}
							// This form of entailment detail will be because we KNOW it's a language container in the actual data
							if (typeof propValue === "object") {
								Object.keys(propValue).forEach(langProp => {
									if (!lang || (lang && langProp === lang)) {
										const langArray = propValue[langProp];
										langArray.forEach(langArrayValue => {
											// Actual data will be a string if only one instance of language exists
											if (typeof data[prop][langProp] === "string") {
												delete data[prop][langProp];
											}
											else {
												const langIndex = data[prop][langProp].findIndex((val) => val === langArrayValue);
												data[prop][langProp].splice(langIndex, 1);
											}
										});
									}
								});
							}
						}
						// If processing just a limited set of properties only remove those entailments
						// from the entailment list that need removing
						if (affectingProperties && affectingProperties.includes(prop)) {
							if (lang) {
								delete data._entailed[prop][lang];
							}
							else {
								delete data._entailed[prop];
							}
						}
					});
					// If no affecting properties remove all existing entailment from entailment list
					if (!affectingProperties || Object.keys(data._entailed).length === 0) delete data._entailed;
				}
			});
		}

		function processChainItem(chainActions, chainResourceData, chain, lang, entailedProp, subjectIRI) {
			const chainMember = chain[0];
			const property = internalContext._context.byIRI[chainMember];
			chainResourceData.forEach(resource => {
				const id = resource.graph + "___" + resource.iri;
				const data = loadedChannels[id];
				// Does this object contain the nth item in the chain?
				if (data && data[property]) {
					// Is this the end of the chain? If it is the property values are those we want
					if (chain.length === 1) {
						const propertyType = internalContext._context.propertyType[property];
						// We're assuming an @set container here - need to fix that
						if (propertyType === "@id") {
							data[property].forEach(propValue => {
								const entailObject = patcher.createLDadd();
								entailObject.setSubject(patcher.createURI(subjectIRI));
								entailObject.setPredicate(patcher.createURI(entailedProp));
								entailObject.setObject(patcher.createURI(propValue));
								entailObject.setGraph(patcher.createURI(resource.graph));
								const subjectId = resource.graph + "___" + subjectIRI;
								chainActions.push({
									subjectId,
									action: entailObject
								});
							});
						}
						else {
							const containerType = internalContext._context.propertyContainer[property];
							if (containerType === "@language") {
								Object.keys(data[property]).forEach(propLang => {
									if (!lang || (lang && propLang === lang)) {
										const entailObject = patcher.createLDadd();
										entailObject.setSubject(patcher.createURI(subjectIRI));
										entailObject.setPredicate(patcher.createURI(entailedProp));
										entailObject.setObject(patcher.createLiteral(escapeText(data[property][propLang]), propLang));
										entailObject.setGraph(patcher.createURI(resource.graph));
										const subjectId = resource.graph + "___" + subjectIRI;
										chainActions.push({
											subjectId,
											action: entailObject
										});
									}
								});
							}
						}
					}
					else {
						// The set of resources to now check for the next part of the chain
						const chainedResources = Array.isArray(data[property]) ? data[property] : [data[property]];
						const chainedResourcesSet = chainedResources.map(chainedResource => {
							return {
								graph: resource.graph,
								iri: chainedResource
							};
						});
						const assignedSubjectIRI = subjectIRI ? subjectIRI : resource.iri;
						// Test next entry in chain	
						processChainItem(chainActions, chainedResourcesSet, chain.slice(1), lang, entailedProp, assignedSubjectIRI);
					}
				}
			});
		}

		function processChainItemBackwards(chainResourceData, chain, entailedProp, resourceList, values) {
			const chainMember = chain[chain.length - 1];
			const property = internalContext._context.byIRI[chainMember];
			chainResourceData.forEach(resource => {
				const id = resource.graph + "___" + resource.iri;
				const data = loadedChannels[id];
				if (data) {
					// Get last item in chain
					// Does this object contain the nth item in the chain?
					if (data[property]) {
						// Is this the start of the chain?
						if (chain.length === 1) {
							const inResourceList = resourceList.some(resourceListItem =>
								resourceListItem.graph === resource.graph && resourceListItem === data.id);
							if (!inResourceList) {
								resourceList.push({
									graph: resource.graph,
									iri: data.id
								});
							}
						}
						else {
							const prevChainMember = chain[chain.length - 2];
							const prevProperty = internalContext._context.byIRI[prevChainMember];
							// The set of resources to now check for the next part back in the chain
							// They are object with the previous chain member as a property and this chain member as the value of it
							// We always pass back an array of IRIs
							const chainedResources = Object.keys(loadedChannels)
								.filter(channel => loadedChannels[channel][prevProperty] && channel.startsWith(resource.graph))
								.filter(channel => {
									const lc = loadedChannels[channel];
									if (Array.isArray(lc[prevProperty])) {
										return lc[prevProperty].includes(resource.iri);
									}
									else return lc[prevProperty] === resource.iri;
								})
								.map(channel => {
									const lc = loadedChannels[channel];
									return {
										graph: resource.graph,
										iri: lc.id
									};
								});
							if (chainedResources.length > 0) {
								// Get the values from the end of the chain
								const assignedValues = values ? values : data[property];
								// Next entry back in the chain	
								chain.pop();
								processChainItemBackwards(chainedResources, chain, entailedProp, resourceList, assignedValues);
							}
						}
					}
				}
			});
		}

		function isEmpty(obj) {
			if (typeof obj === "string" && obj === "{}") return true;
			return Object.keys(obj).length === 0;
		}

		function isPropertyLoaded(iri, graphIRI, property) {
			const id = graphIRI + "___" + iri;
			// No options so full load
			const options = boundObject[id]._loadOptions;
			if (isEmpty(options) || !options.predicates) return true;
			return options.predicates.includes(property);
		}

		function processLoadedData(dataSet, catchupRef, loadOptions = {}) {
			if (!dataSet || (dataSet && dataSet.length === 0)) return;
			const resourceSet = cloneDeep(dataSet);
			const catchup = catchupRef ? [...catchupRef] : catchupRef;
			resourceSet.forEach(data => {
				// Making a big assumption about .id for now
				const iri = data.id;
				// There is always the possibility that multiple requests for the same resource are in flight at the same time
				// In that case we don't want a partial load to overwrite a full load if the full returns first
				const id = data.inNamedGraph + "___" + iri;
				if (!boundObject[id] || !boundObject[id]._loadOptions || !isEmpty(boundObject[id]._loadOptions)) {
					// If we have already partially loaded this IRI there may be bound events so don't just reset to {}
					if (!boundObject[id]) {
						boundObject[id] = {};
						Object.defineProperty(boundObject[id], "_iri", {
							value: iri,
							writable: true
						});
					}
					Object.defineProperty(boundObject[id], "_loadOptions", {
						value: loadOptions,
						writable: true
					});
					// Revision number may not exist if no revisions made yet so initialise to zero
					const revisionNo = data.revisionNo ? data.revisionNo : 0;
					if (data["@context"]) throw new Error("Context included!!!");
					loadedChannels[id] = data;
					lastSyncedRevisionNo[id] = revisionNo;
					if (catchup && catchup.length > 0) processCatchup(iri, data.inNamedGraph, catchup);
					// Process any stored updates to ensure in sync with feed of events
					processStoredMessages(iri, data.inNamedGraph, revisionNo);
				}
				else {
					if (iri === undefined) {
						throw new Error("Problem in processing loaded data. IRI undefined");
					}
				}
			});
		}

		function processNewData(data, graphIRI) {
			// Making a big assumption about .id for now
			const iri = data.id;
			const id = graphIRI + "___" + iri;
			boundObject[id] = {};
			Object.defineProperty(boundObject[id], "_iri", {
				value: iri,
				writable: true
			});
			Object.defineProperty(boundObject[id], "_loadOptions", {
				value: {},
				writable: true
			});
			if (data["@context"]) throw new Error("Context included!!!");
			// We create a temporary creation date for local resources (mainly to help with sorting)
			if (!data.created) {
				const date = new Date();
				const isoDate = date.toISOString();
				data._created = isoDate;
			}
			loadedChannels[id] = data;
			lastSyncedRevisionNo[id] = 0;
		}

		// iri is the IRI for which we are looking for catchup data
		function processCatchup(iri, graphIRI, catchup) {
			const id = graphIRI + "___" + iri;
			// We process those messages that are revision number less than the first stored revision number if it exists
			let channelRevisions = catchup.find(resource => resource.iri === iri);
			if (!channelRevisions) return;
			if (channelRevisions.revision.length > 0) {
				let upperRevision = -1;
				if (storedMessages[id]) {
					if (storedMessages[id].length > 0) {
						const message = storedMessages[id][0];
						upperRevision = message.revisionNo;
					}
				}
				channelRevisions.revision
					.filter(revision => upperRevision === -1 || revision.revisionNo < upperRevision)
					.forEach(revision => processIncomingRevision(revision, id, false));
			}
		}

		function processStoredMessages(iri, graphIRI, revisionNo) {
			const id = graphIRI + "___" + iri;
			if (storedMessages[id] != null) {
				for (let i = 0; i < storedMessages[id].length; i++) {
					const message = storedMessages[id][i];
					// Process only revisions greater than that which we have caught up to
					if (message.revisionNo > revisionNo) {
						processIncomingRevision(message, iri, false, false);
					}
				}
			}
			// Processed them all so clear things
			storedMessages[id] = [];
		}

		// To reduce load effort predicates can be specified as an array of IRIs
		// And language as a lang value
		// Specifying these will limit the retrieved data
		async function retrieveDataFromAPI(resourceData, predicates, language, tests, countPredicates, includeAuth, resourceFilters, excludeDeprecated = false) {
			try {
				if (resourceData.length === 0) return {
					data: [],
					catchupData: []
				};
				let resources = [];
				let graphResources = {};
				resourceData.forEach(resource => {
					const graphIRI = resource.graph;
					const iri = resource.iri;
					if (!graphResources[graphIRI]) graphResources[graphIRI] = [];
					graphResources[graphIRI].push(iri);
				});
				Object.keys(graphResources).forEach(graphResource => {
					resources.push({
						graph: graphResource,
						iri: graphResources[graphResource]
					});
				});
				const payload = {
					socketId: socket.id,
					synchronise: true,
					subscription: activeSubscription,
					predicates,
					language,
					tests,
					...(excludeDeprecated) && { excludeDeprecated: true },
					countPredicates: countPredicates,
					includeAuth,
					resourceFilters,
					resources
				};
				let response = await apiManager.sendData("/resource", payload, { accept: "application/ld+json" });
				const data = await response.json();
				return data;
			}
			catch (e) {
				throw new Error(e.message);
			}
		}

	}

	// Return the constructor
	return RDFedit;

})();

export default new GS();